import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { isFreePlan } from '../../utils/planAccess';
import { toAssetUrl } from '../../utils/assetBase';
import UpgradeModal from '../../components/UpgradeModal';
import { fmtTime, fmtDateTime, fmtDateTimeNum, fmtDateTimeNumShort, fmtAgo } from '../../utils/dateFormat';
import { printBill, printKitchen, printGift, getReceiptPrinterName, canFiscalRelay, fiscalForward, getKitchenQtyX, getKitchenConfirmExit } from '../../utils/printService';
import { confirm } from '../../components/ConfirmDialog';
import { enqueueOp, takeQueuedDraft } from '../../utils/offlineQueue';
import { getOfflineAuth } from '../../utils/offlineAuth';
import { readCache, offlineEnabled, graceValidToday, getOfflineSettings } from '../../utils/offlineCache';
import { isOffline } from '../../utils/netStatus';
import { tapxCardCharge } from '../../utils/tapxCharge';
import { useFoodDeliveryInfo } from '../../utils/foodDelivery';

const trimQty = (value, digits = 3) => Number(value || 0).toFixed(digits).replace(/\.?0+$/, '');

// Saatlıq məhsul üçün keçən vaxt: 9000 san → "2 saat 30 dəq"
const fmtElapsed = (sec) => {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h} saat ${m} dəq` : `${m} dəq`;
};

const formatQty = (qty, saleType) => {
    if ((saleType || 'piece') === 'piece') {
        return `${Math.round(Number(qty || 0))}əd`;
    }

    if (saleType === 'weight') {
        return `${trimQty(qty)}kq`;
    }

    if (saleType === 'liter') {
        return `${trimQty(qty)}lt`;
    }

    return trimQty(qty);
};

const getSaleTypeBadge = (saleType) => {
    if (saleType === 'weight') return 'kq';
    if (saleType === 'liter') return 'lt';
    return 'əd';
};

// Set komponentlərini qruplaşdır — eyni set_group_id-li sətirləri tək sətr kimi göstər
const groupSetItems = (items = []) => {
    const groups = new Map();
    const list = [];
    for (const it of items) {
        if (it.set_group_id) {
            let g = groups.get(it.set_group_id);
            if (!g) {
                g = {
                    _isSetGroup: true,
                    id: `setgroup_${it.set_group_id}`,
                    item_name: it.note || 'Set',
                    note: '',
                    sale_type: 'piece',
                    _total: 0,
                    _minCount: Infinity
                };
                groups.set(it.set_group_id, g);
                list.push(g);
            }
            g._total += Number(it.qty) * Number(it.unit_price);
            const unit = Number(it.set_unit_qty || 0);
            const cnt = unit > 0 ? Number(it.qty) / unit : 1;
            if (cnt < g._minCount) g._minCount = cnt;
        } else {
            list.push(it);
        }
    }
    for (const g of list) {
        if (g._isSetGroup) {
            g.qty = Math.max(1, Math.round(g._minCount || 1));
            g.unit_price = g.qty > 0 ? g._total / g.qty : 0;
        }
    }
    return list;
};

const getQuickValues = (saleType) => {
    if (saleType === 'weight') return ['0.25', '0.5', '1', '1.5', '2'];
    if (saleType === 'liter') return ['0.33', '0.5', '1', '1.5', '2'];
    return ['1', '2', '3', '4', '5'];
};

const getCategoryAccent = (index) => {
    const accents = [
        { shell: 'from-sky-50 to-cyan-100 border-sky-200', badge: 'bg-sky-100 text-sky-700', icon: '🥗' },
        { shell: 'from-amber-50 to-orange-100 border-amber-200', badge: 'bg-amber-100 text-amber-700', icon: '🍽️' },
        { shell: 'from-emerald-50 to-lime-100 border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', icon: '🥤' },
        { shell: 'from-rose-50 to-pink-100 border-rose-200', badge: 'bg-rose-100 text-rose-700', icon: '🍰' }
    ];

    return accents[index % accents.length];
};

// Kateqoriya adına görə ağıllı emoji — sıraya görə deyil, MƏNAYA görə.
// Tapılmasa neytral 🍽️ (heç vaxt yanıldıcı tort verməz).
// Sıra vacibdir: daha spesifik açar sözlər əvvəldə (məs. "məzə" "pivə"dən əvvəl).
const CATEGORY_ICON_RULES = [
    // "qəlyanaltı" (appetizer) "qəlyan" (kalyan)-dan ƏVVƏL yoxlanmalıdır!
    [['qəlyanaltı', 'qelyanalti'], '🥗'],
    [['məzə', 'meze', 'snack', 'çərəz', 'cerez'], '🥜'],
    [['şorba', 'sorba', 'soup'], '🍲'],
    [['sous', 'souce', 'ketçup', 'ketcup'], '🥫'],
    [['sosis', 'kolbasa', 'sausage', 'hotdog', 'hot dog'], '🌭'],
    [['qarnir', 'garnir', 'garnish', 'düyü', 'duyu', 'pilaf', 'plov'], '🍚'],
    [['xəmir', 'xemir', 'pizza', 'lavaş', 'lavas', 'çörək', 'corek', 'börək', 'borek', 'qutab', 'pide'], '🥟'],
    [['meyvə', 'meyve', 'fruit', 'meyvələr'], '🍓'],
    [['viski', 'whisky', 'whiskey'], '🥃'],
    [['tequila', 'tekila'], '🥃'],
    [['konyak', 'brendi', 'brandy', 'cognac'], '🥃'],
    [['araq', 'vodka', 'votka'], '🍸'],
    [['şərab', 'serab', 'wine', 'vino'], '🍷'],
    [['şampan', 'sampan', 'champagne'], '🥂'],
    [['kokteyl', 'kokteil', 'cocktail'], '🍹'],
    [['pivə', 'pive', 'beer', 'çəllək', 'cellek', 'draft'], '🍺'],
    [['çay', 'cay', 'tea'], '🍵'],
    [['qəhvə', 'qehve', 'kofe', 'coffee', 'espresso', 'kapuçino', 'kapucino'], '☕'],
    [['qəlyan', 'qelyan', 'hookah', 'nargile', 'nargilə', 'kalyan'], '💨'],
    [['balıq', 'baliq', 'fish', 'suşi', 'susi', 'sushi'], '🐟'],
    [['toyuq', 'chicken', 'nagets', 'nuggets'], '🍗'],
    [['kabab', 'kebab', 'manqal', 'mangal', 'şaşlıq', 'saslik', 'grill', 'barbik'], '🍖'],
    [['burger', 'hamburger'], '🍔'],
    [['fri', 'fries', 'fastfood', 'fast food', 'kartof'], '🍟'],
    [['şirniyyat', 'sirniyyat', 'desert', 'dessert', 'tort', 'kek', 'pasta', 'sweet'], '🍰'],
    [['dondurma', 'ice cream', 'icecream', 'morojna'], '🍦'],
    [['cola', 'kola', 'fanta', 'sprite', 'limonad', 'şirə', 'sire', 'juice', 'şərbət', 'serbet', 'sərin', 'serin', 'içki', 'icki', 'drink', 'soda', 'enerji', 'energy'], '🥤'],
    [['salat', 'salad', 'salata', 'soyuq', 'soguk', 'appetizer', 'starter'], '🥗'],
    [['isti', 'hot', 'steak', 'bifştek', 'bifstek', 'ana yemək', 'main', 'yemək', 'yemek', 'dish'], '🥘'],
];

const getCategoryIcon = (name) => {
    const n = String(name || '').toLocaleLowerCase('az');
    for (const [keys, emoji] of CATEGORY_ICON_RULES) {
        if (keys.some(k => n.includes(k))) return emoji;
    }
    return '🍽️'; // neytral — heç vaxt yanıldıcı deyil
};

const WEBVIEW_PRINT_TIMEOUT_MS = 15000;
const getReceiptTitle = (currentUser) => (
    currentUser?.tenant_plan === 'pro'
        ? (currentUser?.tenant_name || 'Fiscal POS')
        : 'Fiscal POS'
);
const DEFAULT_RECEIPT_LOGO = `${window.location.origin}/FiscalPOS_black.png`;

const getReceiptLogoUrl = (currentUser) => {
    if (isFreePlan(currentUser?.tenant_plan)) return null;
    if (currentUser?.receipt_logo_url) return toAssetUrl(currentUser.receipt_logo_url);
    return DEFAULT_RECEIPT_LOGO;
};

const toMoney = (value) => Number(value || 0);
const formatMoney = (value) => `${toMoney(value).toFixed(2)} ₼`;

const buildReceiptText = (currentOrder, currentUser, takeaway = false) => {
    const title = getReceiptTitle(currentUser);
    const printedAt = fmtDateTime(new Date());
    const subtotal = toMoney(currentOrder?.subtotal);
    const servicePercent = toMoney(currentOrder?.service_percent);
    const serviceAmount = toMoney(currentOrder?.service_amount);
    const discountAmount = toMoney(currentOrder?.discount_amount);
    const appliedDeposit = toMoney(currentOrder?.applied_deposit);
    const grandTotal = toMoney(currentOrder?.grand_total || subtotal + serviceAmount - discountAmount + appliedDeposit);

    const lines = [
        title,
        'Musteri ceki',
        '-------------------------------',
        `Telefon: ${currentOrder?.tenant_phone || '-'}`,
        `Masa: ${currentOrder?.table_name || '-'}`,
        `Sifaris: #${currentOrder?.id || '-'}`,
        `Ofisiant: ${currentOrder?.waiter_name || '-'}`,
        `Tarix: ${printedAt}`,
        '-------------------------------'
    ];

    groupSetItems(currentOrder?.items || []).forEach((item) => {
        const itemTotal = parseFloat(item.unit_price) * Number(item.qty);
        const qtyLabel = item._isSetGroup ? `${item.qty}ed` : formatQty(item.qty, item.sale_type);
        lines.push(item._isSetGroup ? `[SET] ${item.item_name}` : item.item_name);
        lines.push(`  ${qtyLabel} x ${parseFloat(item.unit_price).toFixed(2)} = ${itemTotal.toFixed(2)} AZN`);
        if (!item._isSetGroup && item.note) {
            lines.push(`  Qeyd: ${item.note}`);
        }
    });

    lines.push('-------------------------------');
    lines.push(`Cem: ${subtotal.toFixed(2)} AZN`);
    lines.push(`${takeaway ? `Takeaway ucun elave ${trimQty(servicePercent, 2)}%` : `${trimQty(servicePercent, 2)}% Servis Haqqi`}: ${serviceAmount.toFixed(2)} AZN`);
    lines.push(`Depozit: ${appliedDeposit.toFixed(2)} AZN`);
    lines.push(`Endirim: ${discountAmount.toFixed(2)} AZN`);
    lines.push(`Umumi cem: ${grandTotal.toFixed(2)} AZN`);

    if (currentOrder?.note) {
        lines.push('-------------------------------');
        lines.push(`Umumi qeyd: ${currentOrder.note}`);
    }

    lines.push('');
    lines.push('Tesekkur edirik!');
    lines.push('Powered by www.fiscalbusiness.net');

    return lines.join('\n');
};

const hasWebViewBridge = () => Boolean(window.chrome?.webview?.postMessage);

// Mətbəx vərəqi — marker formatı (C# PrintKitchenReceipt parse edir):
//   @T@ başlıq (böyük bold center) · @C@ center · @R@ dashed xətt
//   @LR@sol|sağ iki sütun (məhsul sol, miqdar sağ) · @I@ ingredient (kiçik, girinti)
//   @S@ set başlığı · prefix yoxdursa normal sol sətr
// Mətbəx miqdar formatı: superadmin "X" seçibsə ədəd suffiksi "X" olur (məs. 1X), yoxsa "1əd"
const kitchenQtyLabel = (qty, saleType, qtyX) => {
    if (qtyX && (saleType || 'piece') === 'piece') return `${Math.round(Number(qty || 0))}X`;
    return formatQty(qty, saleType);
};

// Mətbəx çekində texkarta inqredientinin OXUNAQLI miqdarı:
//   • kq < 1 → qram (0.1 kg → 100 q), lt < 1 → ml
//   • ədəd: 1 → "1 ədəd", 0.5 → "yarım", 0.25 → "1/4", 0.75 → "3/4", 0.33/0.66 → 1/3, 2/3
const fmtIngredientQty = (total, unit) => {
    const t = Number(total) || 0;
    const u = String(unit || '').toLowerCase();
    const trim = (v) => Number(v).toFixed(3).replace(/\.?0+$/, '');
    if (u === 'kg' || u === 'kq') return t < 1 ? `${Math.round(t * 1000)} q` : `${trim(t)} kq`;
    if (u === 'gram' || u === 'q' || u === 'qram') return `${trim(t)} q`;
    if (u === 'litr' || u === 'lt' || u === 'liter') return t < 1 ? `${Math.round(t * 1000)} ml` : `${trim(t)} lt`;
    if (u === 'ml') return `${trim(t)} ml`;
    if (u === 'eded' || u === 'ədəd' || u === 'əd' || u === '') {
        if (Number.isInteger(t)) return `${t} ədəd`;
        const near = (a, b) => Math.abs(a - b) < 0.02;
        if (t < 1) {
            if (near(t, 0.5)) return 'yarım';
            if (near(t, 0.25)) return '1/4';
            if (near(t, 0.75)) return '3/4';
            if (near(t, 1 / 3)) return '1/3';
            if (near(t, 2 / 3)) return '2/3';
        }
        return `${trim(t)} ədəd`;
    }
    return `${trim(t)}${unit ? ' ' + unit : ''}`;
};

// Set komponentinin mətbəx miqdarı: qty PORSIYA-dır. 'eded' → real ədəd (qty×porsiyadakı ədəd),
// 'pors' → porsiya kimi. Beləcə "Toyuq kababı 4 əd" (pors yox) düzgün çıxır.
const setKitchenQtyLabel = (item, qtyX) => {
    const st = item.sale_type || 'piece';
    if (st !== 'piece') return kitchenQtyLabel(item.qty, st, qtyX);
    if (String(item.set_unit_mode || 'eded') === 'pors') {
        const p = Number(item.qty || 0).toFixed(3).replace(/\.?0+$/, '');
        return `${p} pors`;
    }
    const pp = Math.max(1, Number(item.portion_pieces) || 1);
    const pieces = Math.round(Number(item.qty || 0) * pp * 100) / 100; // 3.333×3=9.999 → 10
    if (Number.isInteger(pieces)) return `${pieces}${qtyX ? 'X' : 'əd'}`;
    // Kəsr (banket «1,5 hər 6 nəfərə» → 7,5): yuvarlaqlaşdırma YOX — anbarla eyni rəqəm görünsün
    const comma = (v) => String(Math.round(v * 100) / 100).replace('.', ',');
    return pp === 1 ? `${comma(pieces)} pors` : `${comma(pieces)} əd`;
};

const buildKitchenTicketText = (currentOrder, group, qtyX = false) => {
    // Kompakt numerik format — dar kağızda tarix/saat sətri aşıb bir-birinə girməsin
    const printedAt = fmtDateTimeNumShort(new Date());
    const lines = [];

    // Başlıq: printer label
    // Fast food: gündəlik sıra nömrəsi başda NƏHƏNG (yalnız rəqəm, prefiks yox) — @N@ marker
    if (currentOrder?.daily_number) lines.push(`@N@${currentOrder.daily_number}`);
    lines.push(`@T@${group?.printer_label || group?.printer || 'Mətbəx'}`);
    lines.push(`@C@${currentOrder?.table_name || '-'}`);
    if (currentOrder?.opened_at) lines.push(`@C@Acilis: ${fmtDateTimeNumShort(currentOrder.opened_at)}`);
    lines.push(`@C@Cap: ${printedAt}`);
    lines.push('@R@');

    const items = group?.items || [];

    // Set komponentlərini set_group_id üzrə ayır, qalanlar adi
    const setGroups = new Map();   // set_group_id → { name, items: [] }
    const normalItems = [];
    for (const it of items) {
        if (it.set_group_id) {
            let g = setGroups.get(it.set_group_id);
            if (!g) {
                // note "Set: X" / "Banket: X (20 nəfər) • porsiya" → yalnız adı (porsiya hissəsi + prefiks çıxarılır ki, başlıq bir sətrə sığsın, aşağıya sarınıb üst-üstə düşməsin)
                const setName = (it.note || 'Set').replace(/^(Set|Banket):\s*/i, '').split(' • ')[0];
                g = { name: setName, items: [] };
                setGroups.set(it.set_group_id, g);
            }
            g.items.push(it);
        } else {
            normalItems.push(it);
        }
    }

    const renderItem = (item) => {
        const qtyLabel = item.set_group_id
            ? setKitchenQtyLabel(item, qtyX)
            : (item.qty_label || kitchenQtyLabel(item.qty, item.sale_type, qtyX));
        lines.push(`@LR@${item.item_name}|${qtyLabel}`);
        // İngredientlər (kiçik, girintili) — məs: "20 qram duz", "100 q toyuq əti"
        (item.ingredients || []).forEach((ing) => {
            lines.push(`@I@${fmtIngredientQty(ing.total, ing.unit)} ${ing.name}`);
        });
        if (item.note && !item.set_group_id) {
            // Qeyddə Enter (yeni sətir) ola bilər → hər sətir ayrıca @I@ olsun.
            // Yoxsa ikinci sətir markersiz qalır və printer onu məhsul adı kimi iri/qalın çap edir.
            String(item.note).split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
                .forEach((s, i) => lines.push(`@I@${i === 0 ? 'Qeyd: ' : '  '}${s}`));
        } else if (item.note && item.set_group_id) {
            // Banket komponenti — yalnız porsiya hissəsini göstər (məs. "9 pors × 3 əd")
            const portionPart = item.note.split(' • ')[1];
            if (portionPart) lines.push(`@I@${portionPart}`);
        }
    };

    // Setlər
    for (const g of setGroups.values()) {
        lines.push(`@S@🎁 ${g.name}`);
        g.items.forEach(renderItem);
        lines.push('@R@');
    }

    // Adi məhsullar
    normalItems.forEach((item) => {
        renderItem(item);
        lines.push('@R@');
    });

    // Footer
    lines.push(`@C@${currentOrder?.waiter_name || '-'}`);
    lines.push(`@C@${currentOrder?.table_name || '-'}`);
    lines.push(`@C@#${currentOrder?.id || '-'}`);

    return lines.join('\n');
};

// LƏĞV çeki — mətbəxə getmiş məhsul silinib/azaldılıbsa Zala qayıdanda çıxır.
// @X@ = üstündən xətli sətir (C# host: USB→strikeout font, LAN→ESC/POS reverse).
const buildKitchenCancelTicketText = (currentOrder, group, qtyX = false) => {
    const printedAt = fmtDateTimeNumShort(new Date());
    const lines = [];
    lines.push(`@T@${group?.printer_label || group?.printer || 'Mətbəx'}`);
    lines.push(`@C@${currentOrder?.table_name || '-'}`);
    lines.push(`@C@Cap: ${printedAt}`);
    lines.push('@R@');
    (group?.items || []).forEach((item) => {
        const qtyLabel = item.qty_label || kitchenQtyLabel(item.qty, item.sale_type, qtyX);
        lines.push(`@X@${item.item_name}|${qtyLabel}`);
    });
    lines.push('@R@');
    lines.push(`@C@${currentOrder?.waiter_name || '-'}`);
    lines.push(`@C@${currentOrder?.table_name || '-'}`);
    lines.push(`@C@#${currentOrder?.id || '-'}`);
    lines.push('@T@LƏĞV EDİLMƏ');
    return lines.join('\n');
};

const sendWebViewPrintRequest = (message, responseType = 'print-bill-result') => new Promise((resolve, reject) => {
    if (!hasWebViewBridge()) {
        reject(new Error('Bu funksiya yalnız Windows tətbiqində işləyir.'));
        return;
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const timeoutId = window.setTimeout(() => {
        window.chrome.webview.removeEventListener('message', handleMessage);
        reject(new Error('Çap cavabı gözlənilən vaxtda gəlmədi.'));
    }, WEBVIEW_PRINT_TIMEOUT_MS);

    const handleMessage = (event) => {
        const payload = event?.data;
        if (!payload || payload.type !== responseType || payload.requestId !== requestId) {
            return;
        }

        window.clearTimeout(timeoutId);
        window.chrome.webview.removeEventListener('message', handleMessage);

        if (payload.ok) {
            resolve(payload);
            return;
        }

        reject(new Error(payload.error || 'Çap baş tutmadı.'));
    };

    window.chrome.webview.addEventListener('message', handleMessage);
    window.chrome.webview.postMessage({
        ...message,
        requestId
    });
});

const WaiterOrder = () => {
    const [syncing, setSyncing] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const { orderId, tableId } = useParams();
    const [searchParams] = useSearchParams();
    const selectedWaiterId = searchParams.get('waiter'); // set when admin/kassir opens table
    const navigate = useNavigate();
    const { user } = useAuthStore();

    const [order, setOrder] = useState(null);
    // Offline-da mövcud sifarişə gözləyən əlavələr (delta sətirlər; server item-lərinə toxunulmur)
    const [offAdds, setOffAdds] = useState([]);
    // Online vurulub offline-da mətbəxə göndərilmiş server sətirləri: { [rowId]: printed_qty }
    const [offPrinted, setOffPrinted] = useState({});

    // Set qruplaması + offline delta-ların server sətirlərinə BİRLƏŞDİRİLMƏSİ (tək sətir göstərilsin)
    const displayOrderItems = useMemo(() => {
        let items = (order?.items || []).map((it) => (offPrinted[it.id] != null
            ? { ...it, printed_qty: Math.max(Number(it.printed_qty || 0), Number(offPrinted[it.id])) }
            : it));
        // Offline əlavələri (offAdds) uyğun server sətrinə birləşdir; uyğun yoxdursa ayrıca "yeni" sətir
        if (offAdds && offAdds.length) {
            const remaining = offAdds.map((a) => ({ ...a }));
            // Server variantlı sətirdə variant adını QEYDƏ yazır ("Böyük" / "Böyük — soyuq") →
            // offline əlavənin effektiv qeydi də eyni qayda ilə qurulur, yoxsa ayrıca «yeni» sətir görünürdü.
            const effNote = (a) => a._storedNote || [a._variantName, a.note].filter(Boolean).join(' — ') || null;
            const sameKey = (a, it) =>
                String(a.item_id) === String(it.item_id) &&
                String(a._variantId || a.variant_id || '') === String(it.variant_id || it._variantId || '') &&
                Number(a.unit_price) === Number(it.unit_price) && effNote(a) === (it.note || null) && !it.set_group_id;
            items = items.map((it) => {
                const mi = remaining.findIndex((a) => sameKey(a, it));
                if (mi >= 0) {
                    const a = remaining.splice(mi, 1)[0];
                    return { ...it, _serverQty: Number(it.qty), _pendingDelta: Number(a.qty), qty: Number(it.qty) + Number(a.qty) };
                }
                return it;
            });
            items = [...items, ...remaining.map((a) => ({ ...a, _pendingOnly: true }))];
        }
        const groups = new Map();
        const list = [];
        for (const it of items) {
            if (it.set_group_id) {
                let g = groups.get(it.set_group_id);
                if (!g) {
                    g = {
                        _isSetGroup: true,
                        id: `setgroup_${it.set_group_id}`,
                        set_group_id: it.set_group_id,
                        set_id: it.set_id || null,
                        item_name: it.note || 'Set',
                        note: '',
                        sale_type: 'piece',
                        kitchen_printed: 0,
                        _total: 0,
                        _minCount: Infinity
                    };
                    groups.set(it.set_group_id, g);
                    list.push(g);
                }
                g._total += Number(it.qty) * Number(it.unit_price);
                const unit = Number(it.set_unit_qty || 0);
                const cnt = unit > 0 ? Number(it.qty) / unit : 1;
                if (cnt < g._minCount) g._minCount = cnt;
                if (Number(it.kitchen_printed || 0)) g.kitchen_printed = 1;
            } else {
                list.push(it);
            }
        }
        for (const g of list) {
            if (g._isSetGroup) {
                g.qty = Math.max(1, Math.round(g._minCount || 1));
                g.unit_price = g.qty > 0 ? g._total / g.qty : 0;
            }
        }
        return list;
    }, [order?.items, offAdds, offPrinted]);
    const [menu, setMenu] = useState([]);
    const [popularQtys, setPopularQtys] = useState({}); // {item_id: ['5','10','15',...]} — dinamik sürətli düymələr
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState(null);
    const [search, setSearch] = useState('');
    // Maya (cost) — admin sifarişin maya dəyərini görsün
    const [mayaOpen, setMayaOpen] = useState(false);
    const [mayaData, setMayaData] = useState(null);
    const [mayaLoading, setMayaLoading] = useState(false);
    // WhatsApp — sifarişi WhatsApp ilə göndər (admin)
    const [waModal, setWaModal] = useState(false);
    const [waNum, setWaNum] = useState('');
    const [waSending, setWaSending] = useState(false);
    const [waHistory, setWaHistory] = useState(() => {
        try { return JSON.parse(localStorage.getItem('wa_recent_nums') || '[]'); } catch { return []; }
    });
    // Axtarış aktiv olanda açıq kateqoriya olmasın → axtarış silinəndə HƏMİŞƏ
    // kateqoriyalar grid-i qayıdır (axtarış nəticələri qalmır). Bütün təmizləmə
    // yollarını (backspace, ✕, Esc) əhatə edir.
    useEffect(() => { if (search && activeCat !== null) setActiveCat(null); }, [search, activeCat]);
    const [note, setNote] = useState('');
    const [timeItemId, setTimeItemId] = useState(null); // tək məhsulun vaxt detalı (kassir/admin)
    const [showAllTimes, setShowAllTimes] = useState(false); // hamısını birdən göstər
    const [cartPanelCollapsed, setCartPanelCollapsed] = useState(false); // mobil: aşağı paneli yığ
    const [, setNowTick] = useState(0);                 // dinamik "X dəq əvvəl" üçün re-render
    const [showNote, setShowNote] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [posBusy, setPosBusy] = useState(false);
    const [posTerminal, setPosTerminal] = useState(false); // kassa POS terminala qoşuludur?
    // POS ödəniş prosesi popup-u: { open, message, closable, error, done }
    const [posModal, setPosModal] = useState({ open: false, message: '', closable: false, error: false, done: false });
    const [kitchenQtyX, setKitchenQtyX] = useState(false);  // mətbəx çekində miqdar "X" formatı
    const [confirmKitchenExit, setConfirmKitchenExit] = useState(false); // Zala qayıdanda soruş (superadmin tick)
    const [exitConfirmModal, setExitConfirmModal] = useState(false);     // popup açıq?

    const [noteModal, setNoteModal] = useState(false);
    const [noteItem, setNoteItem] = useState(null);
    const [itemNote, setItemNote] = useState('');

    const [qtyModal, setQtyModal] = useState({
        open: false,
        mode: 'add',
        saleType: 'weight',
        item: null,
        orderItem: null,
        value: ''
    });

    const [variantModal, setVariantModal] = useState({ open: false, item: null });
    const [openPriceModal, setOpenPriceModal] = useState({ open: false, item: null, price: '' });
    const [customItemModal, setCustomItemModal] = useState(false);
    const [customItem, setCustomItem] = useState({ name: '', price: '', qty: 1, note: '', printer: '' });
    const [kitchenPrinters, setKitchenPrinters] = useState([]);
    // Banket — qonaq sayı ilə sifarişə əlavə
    const [banquetModal, setBanquetModal] = useState(false);
    const [banquetEnabled, setBanquetEnabled] = useState(false); // superadmin açıbsa göstər
    const [banquets, setBanquets] = useState([]);
    const [bqSel, setBqSel] = useState(null);       // seçilmiş banket obyekti
    const [bqGuests, setBqGuests] = useState(10);
    const [bqBusy, setBqBusy] = useState(false);
    // Sağ panel — "Digər əməliyyatlar" yığcam menyu (hər masaya girəndə minimallaşmış açılır)
    const [moreActionsOpen, setMoreActionsOpen] = useState(false);
    // Çek çap tarixçəsi (müştəri + yarışma çeki — vaxt logu)
    const [printLogModal, setPrintLogModal] = useState(false);
    const [printLog, setPrintLog] = useState([]);
    const [printLogLoading, setPrintLogLoading] = useState(false);

    const openPrintLog = async () => {
        if (!currentOrderId) return;
        setPrintLogModal(true); setPrintLogLoading(true); setPrintLog([]);
        try {
            const res = await api.get(`/orders/${currentOrderId}/print-log`);
            setPrintLog(res.data?.data || []);
        } catch { toast.error('Çek tarixçəsi yüklənmədi.'); }
        finally { setPrintLogLoading(false); }
    };
    // "YYYY-MM-DD HH:mm:ss" (Baku) → "DD-MM-YYYY saat HH:mm"
    const fmtLogTime = (s) => {
        if (!s) return '';
        const [d, t] = String(s).split(' ');
        const [Y, M, D] = (d || '').split('-');
        const [h, mi] = (t || '').split(':');
        return `${D}-${M}-${Y} saat ${h}:${mi}`;
    };

    const openBanquetModal = async () => {
        setBanquetModal(true); setBqSel(null); setBqGuests(10);
        try {
            const res = await api.get('/banquet');
            setBanquets((res.data.data || []).filter(b => Number(b.active) === 1));
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Banketlər yüklənmədi.');
        }
    };

    // Minimum qonaq sayı: kassir/ofisiant/administrator bundan az yaza bilməz, admin yaza bilər (backend də yoxlayır)
    const bqMin = Math.max(0, parseInt(bqSel?.min_guests, 10) || 0);
    const bqCanBelowMin = ['admin', 'superadmin'].includes(user?.role) || String(user?.display_role || '').toLowerCase() === 'admin';
    const bqFloor = bqCanBelowMin ? 1 : Math.max(1, bqMin);
    const bqBelowMin = bqMin > 0 && (parseInt(bqGuests, 10) || 0) < bqMin;
    const selectBanquet = (b) => {
        setBqSel(b);
        const m = Math.max(0, parseInt(b?.min_guests, 10) || 0);
        if (m > 0) setBqGuests((g) => Math.max(parseInt(g, 10) || 0, m));
    };

    const submitBanquet = async () => {
        if (order?._offline) { toast.error('Offline rejimdə banket əlavə edilə bilməz.'); return; }
        if (!bqSel) return toast.error('Banket seçin.');
        const g = Math.max(1, parseInt(bqGuests, 10) || 0);
        if (bqMin > 0 && g < bqMin && !bqCanBelowMin) {
            toast.error(`Bu banket minimum ${bqMin} nəfərlikdir. Daha az sayı yalnız admin yaza bilər.`);
            return;
        }
        setBqBusy(true);
        try {
            const oid = await ensureOrder();
            if (!oid) return;
            const res = await api.post(`/orders/${oid}/banquet`, { banquet_id: bqSel.id, guests: g });
            await loadOrder(oid);
            toast.success(res.data?.msg || 'Banket əlavə edildi.');
            setBanquetModal(false);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Əlavə edilmədi.');
        } finally {
            setBqBusy(false);
        }
    };

    // Printerlər siyahısı — əlavə menyu (manual) üçün mətbəx seçimi
    useEffect(() => {
        let active = true;
        api.get('/printers')
            .then((res) => {
                if (!active) return;
                const list = (res.data?.data || []).filter(p => Number(p.active) === 1);
                setKitchenPrinters(list);
            })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    // Əlavə menyu modalını aç — 1 printer varsa avtomatik seç
    const openCustomModal = () => {
        const printerDefault = kitchenPrinters.length === 1 ? kitchenPrinters[0].name : '';
        setCustomItem({ name: '', price: '', qty: 1, note: '', printer: printerDefault });
        setCustomItemModal(true);
    };
    const [customSaving, setCustomSaving] = useState(false);
    const [reprintConfirm, setReprintConfirm] = useState({ open: false, names: '' });

    const submitCustomItem = async () => {
        if (!customItem.name?.trim()) return toast.error('Ad daxil edin.');
        const price = parseFloat(customItem.price);
        const qty = parseFloat(customItem.qty) || 1;
        if (isNaN(price) || price <= 0) return toast.error('Düzgün qiymət daxil edin.');
        if (order?._offline) {
            const draft = readOffDraft(tableId) || { items: [], note };
            const items = [...(draft.items || [])];
            items.push({
                id: 'off_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
                item_id: null,
                custom_name: customItem.name.trim(),
                custom_printer: customItem.printer || null,
                item_name: customItem.name.trim(),
                sale_type: 'piece',
                qty: Number(qty), unit_price: Number(price), note: customItem.note || null,
                _variantId: null, _variantName: null, kitchen_printed: 0, printed_qty: 0,
            });
            persistOfflineCart(items, draft.note ?? note);
            toast.success(`"${customItem.name}" əlavə edildi.`);
            setCustomItemModal(false);
            setCustomItem({ name: '', price: '', qty: 1, note: '', printer: '' });
            setCustomSaving(false);
            return;
        }
        if (offlineExistingActive()) {
            const line = {
                id: 'off_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
                item_id: null, custom_name: customItem.name.trim(), custom_printer: customItem.printer || null,
                item_name: customItem.name.trim(), sale_type: 'piece',
                qty: Number(qty), unit_price: Number(price), note: customItem.note || null,
                _variantId: null, _variantName: null, kitchen_printed: 0, printed_qty: 0,
            };
            const adds = [...readOffAdds(currentOrderId), line];
            writeOffAdds(currentOrderId, adds); setOffAdds(adds);
            toast.success(`"${customItem.name}" əlavə edildi.`);
            setCustomItemModal(false);
            setCustomItem({ name: '', price: '', qty: 1, note: '', printer: '' });
            return;
        }
        setCustomSaving(true);
        try {
            const oid = await ensureOrder();
            if (!oid) return; // masa bloklandı → geri çıxıldı
            await api.post(`/orders/${oid}/custom-item`, {
                name: customItem.name.trim(),
                price,
                qty,
                note: customItem.note || null,
                printer: customItem.printer || null,
            });
            await loadOrder(oid);
            toast.success(`"${customItem.name}" əlavə edildi.`);
            setCustomItemModal(false);
            setCustomItem({ name: '', price: '', qty: 1, note: '', printer: '' });
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        } finally {
            setCustomSaving(false);
        }
    };

    const [unlockModal, setUnlockModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [cancelModal, setCancelModal] = useState(false);
    const [moveModal, setMoveModal] = useState(false);
    const [movingOrder, setMovingOrder] = useState(false);
    const [moveTargets, setMoveTargets] = useState([]);
    const [selectedMoveTableId, setSelectedMoveTableId] = useState('');
    const [moveSearch, setMoveSearch] = useState('');
    // Dolu masa ilə birləşdir (eyni modalda ikinci rejim)
    const [moveMode, setMoveMode] = useState('move'); // move | merge
    const [mergeTargets, setMergeTargets] = useState([]);
    const [selectedMergeOrderId, setSelectedMergeOrderId] = useState('');
    const [mergeLoading, setMergeLoading] = useState(false);

    // Ofisiant dəyişdirmə (admin/kassir üçün)
    const [waiterChangeModal, setWaiterChangeModal] = useState(false);
    const [waiterChangeList, setWaiterChangeList]   = useState([]);
    const [waiterChangeSearch, setWaiterChangeSearch] = useState('');
    const [waiterChangeLoading, setWaiterChangeLoading] = useState(false);
    const [waiterChangeSaving, setWaiterChangeSaving]   = useState(false);

    const openWaiterChange = async () => {
        if (!currentOrderId) {
            toast.error('Əvvəlcə sifariş açılmalıdır.');
            return;
        }
        setWaiterChangeSearch('');
        setWaiterChangeLoading(true);
        try {
            const res = await api.get('/auth/waiters');
            setWaiterChangeList((res.data?.data || []).filter(w => w.active));
            setWaiterChangeModal(true);
        } catch {
            toast.error('Ofisiantlar yüklənmədi.');
        } finally {
            setWaiterChangeLoading(false);
        }
    };

    const changeWaiter = async (newWaiterId) => {
        if (!currentOrderId || !newWaiterId) return;
        setWaiterChangeSaving(true);
        try {
            const res = await api.post(`/orders/${currentOrderId}/change-waiter`, { user_id: newWaiterId });
            toast.success(res.data?.msg || 'Ofisiant dəyişdirildi.');
            setWaiterChangeModal(false);
            await loadOrder(currentOrderId);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Dəyişdirilə bilmədi.');
        } finally {
            setWaiterChangeSaving(false);
        }
    };

    const [currentOrderId, setCurrentOrderId] = useState(
        orderId && orderId !== 'undefined' && orderId !== 'null' ? orderId : null
    );

    // ══ OFFLINE REJİM (flag arxasında, additive) ════════════════════════════════
    // Yeni sifariş offline tutulur (lokal draft) → "Offline təsdiqlə" → sync növbəsi →
    // online qayıdanda avtomatik server-ə yüklənir. Online yollar TAM toxunulmaz.
    // netOffline: etibarlı net-status (C# WebView2-də navigator.onLine yanlışdır) + 5 san poll + hadisələr
    const [netOffline, setNetOffline] = useState(isOffline());
    useEffect(() => {
        const sync = () => setNetOffline(isOffline());
        window.addEventListener('online', sync);
        window.addEventListener('offline', sync);
        window.addEventListener('net-status', sync); // netMonitor keçidi — dərhal
        const iv = setInterval(sync, 5000);
        return () => { window.removeEventListener('online', sync); window.removeEventListener('offline', sync); window.removeEventListener('net-status', sync); clearInterval(iv); };
    }, []);
    const [offPayModal, setOffPayModal] = useState({ open: false, method: 'nagd', cash: '', card: '' });
    // Offline bağlamada «🛵 Delivery» (son bilinən icazə; server sinxronda yoxlayır).
    // «Yalnız Food delivery» otağının masasında yeganə növ delivery-dir.
    const fdInfo = useFoodDeliveryInfo();
    const offDeliveryOnly = fdInfo.tenant && fdInfo.tableIds.includes(Number(order?.table_id ?? tableId));
    const foodDelivery = fdInfo.enabled || offDeliveryOnly;
    // Offline-da MÖVCUD (online) sifarişə gözləyən əlavələr (offAdds state yuxarıda elan olunub)
    const offAddKey = (oid) => `offadd:${oid}`;
    const readOffAdds = (oid) => { try { return JSON.parse(localStorage.getItem(offAddKey(oid)) || '[]'); } catch { return []; } };
    const writeOffAdds = (oid, arr) => { try { localStorage.setItem(offAddKey(oid), JSON.stringify(arr)); } catch { /* ignore */ } };
    const clearOffAdds = (oid) => { try { localStorage.removeItem(offAddKey(oid)); } catch { /* ignore */ } };
    // Offline + mövcud açıq sifariş rejimi (yeni offline draft DEYİL)
    const offlineExistingActive = () => offlineEnabled() && graceValidToday() && isOffline() && !!currentOrderId && !order?._offline;
    const offDraftKey = (tid) => `offdraft:${tid}`;
    const readOffDraft = (tid) => { try { return JSON.parse(localStorage.getItem(offDraftKey(tid)) || 'null'); } catch { return null; } };
    const writeOffDraft = (tid, d) => { try { localStorage.setItem(offDraftKey(tid), JSON.stringify(d)); } catch { /* ignore */ } };
    const clearOffDraft = (tid) => { try { localStorage.removeItem(offDraftKey(tid)); } catch { /* ignore */ } };
    const offlineTableName = () => {
        try {
            const c = readCache('/tables');
            const list = Array.isArray(c) ? c : (c?.data || c?.tables || []);
            const t = (Array.isArray(list) ? list : []).find((x) => String(x.id) === String(tableId));
            return t?.name || t?.table_name || (tableId ? `Masa #${tableId}` : 'Masa');
        } catch { return tableId ? `Masa #${tableId}` : 'Masa'; }
    };
    // Lokal səbətdən server order formasında sintetik obyekt (render dəyişmədən işləsin)
    const buildOfflineOrder = useCallback((items, noteVal) => {
        const sub = (items || []).reduce((s, it) => s + Number(it.unit_price || 0) * Number(it.qty || 0), 0);
        return {
            _offline: true,
            id: null,
            table_name: offlineTableName(),
            waiter_name: user?.name || '',
            note: noteVal || '',
            items: (items || []).map((it) => ({ ...it })),
            service_percent: 0, service_amount: 0, discount_amount: 0, applied_deposit: 0,
            grand_total: sub, opened_at: null,
            bill_printed: Number(readOffDraft(tableId)?.bill_printed || 0) === 1 ? 1 : 0, // offline çek çıxıbsa kilid
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableId, user?.name]);
    // Səbəti setOrder + draft-a yaz (növbəyə YOX — növbəyə yalnız "Təsdiqlə"də gedir)
    // TƏHLÜKƏSİZLİK: yalnız YENİ sifariş (currentOrderId yox). Mövcud online sifariş offline
    // açılırsa, draft yaratmaq dublikat sifariş yaradar → bloklanır (read-only baxış).
    const persistOfflineCart = (items, noteVal) => {
        if (currentOrderId) { toast.error('Offline rejimdə mövcud sifarişə dəyişiklik edilə bilməz.'); return; }
        const prevDraft = readOffDraft(tableId);
        // Ofisiantı draft-da saxla ki, masanı yenidən açanda təkrar soruşulmasın
        const waiterId = (selectedWaiterId ? Number(selectedWaiterId) : null)
            || prevDraft?.waiter_id || (user?.id ? Number(user.id) : null);
        writeOffDraft(tableId, { items, note: noteVal || '', waiter_id: waiterId, ...(prevDraft?.bill_printed ? { bill_printed: 1 } : {}) });
        setOrder(buildOfflineOrder(items, noteVal));
    };
    // Açılışda offline draft rejimini qur:
    //  • OFFLINE isə → həmişə (yeni sifariş offline tutulsun)
    //  • ONLINE olsa belə, bu masanın yarımçıq DRAFT-ı varsa → onu göstər (iş itməsin, bitirmək olsun)
    // Bir dəfə draft rejimə girdikdən sonra şəbəkə dəyişsə belə order._offline qalır (divergensiya olmaz).
    useEffect(() => {
        if (currentOrderId) return;                      // mövcud online sifariş
        if (!(offlineEnabled() && graceValidToday())) return; // flag yoxdursa normal online davranış
        let draft = readOffDraft(tableId);
        let hasDraft = draft && Array.isArray(draft.items) && draft.items.length > 0;
        // Offline-da: bu masanın növbədə gözləyən (saxlanmış) sifarişi varsa → onu draft-a geri götür.
        // Hələ serverə getməyib (offline-da flush olmur), ona görə ikiqat olmur; bağlananda tək createAndPay gedir.
        if (!hasDraft && netOffline) {
            const restored = takeQueuedDraft(tableId);
            if (restored) { writeOffDraft(tableId, restored); draft = restored; hasDraft = true; }
        }
        if (!netOffline && !hasDraft) return;            // online + draft yoxdursa → adi online yeni sifariş
        setOrder((prev) => {
            if (prev && prev._offline) return prev;      // artıq offline rejimdədir
            setNote(draft?.note || '');
            return buildOfflineOrder(draft?.items || [], draft?.note || '');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [netOffline, currentOrderId]);

    // Yalnız "waiter" rolu üçün çap düyməsi. Admin/Kassir/Superadmin → Hesabı bağla
    // PIN ilə girən admin/kassir backend-də 'waiter' rolu alır amma is_manager=true olur
    const isManager     = user?.is_manager === true;
    const isWaiterOnly  = user?.role === 'waiter' && !isManager;
    const isSimpleKassir = user?.mode === 'simple_kassir';
    const canCloseTable = (['admin', 'superadmin', 'kassir'].includes(user?.role) || isManager) && !isSimpleKassir;
    const canUnlockKitchen = ['admin', 'superadmin'].includes(user?.role);
    // Admin/kassir (password ilə) və ya PIN ilə girən admin/kassir (role='waiter' + is_manager=true)
    // çek/mətbəx çapından sonra da item silib dəyişə bilər
    const canOverridePrintLock =
        ['admin', 'superadmin', 'kassir'].includes(user?.role) || !!user?.is_manager;
    // Silmə/azaltma bayrağı login-də sessiyaya təyin olunur (ofisiant PIN / kassir PIN toggle-ları).
    // Admin və parol-kassirdə 0 gəlir, ona görə ayrıca rol yoxlaması lazım deyil.
    // «Yalnız baxış» (superadmin: Administrator/Kassir «əlavə edə bilsin» söndürülüb): sifarişin tərkibi
    // dəyişdirilə bilməz (əlavə/artırma/silmə/köçürmə). Ödəniş, hesab bağlama və çap açıqdır. Backend də bloklayır.
    const viewOnly = Number(user?.waiter_view_only || 0) === 1;
    const VIEW_ONLY_MSG = 'Yalnız baxış rejimi: sifarişə məhsul əlavə edə və ya dəyişə bilməzsiniz.';
    const hideDelete = viewOnly || Number(user?.waiter_no_delete || 0) === 1;
    const showKitchenButton = true; // bütün rollar üçün — admin/kassir də mətbəxə göndərə bilsin

    const loadOrder = useCallback(async (oid) => {
        const id = oid || currentOrderId;
        if (!id) return;

        try {
            const res = await api.get(`/orders/${id}`);
            setOrder(res.data.data);
            setNote(res.data.data.note || '');
        } catch (err) {
            const status = err.response?.status;
            if (status === 403) {
                toast.error(err.response?.data?.msg || 'Bu sifariş sizə aid deyil.');
            } else if (status === 404) {
                toast.error('Sifariş tapılmadı.');
            } else {
                toast.error('Sifariş yüklənmədi. Xəta baş verdi.');
            }
            navigate('/waiter/tables');
        }
    }, [currentOrderId, navigate]);

    // Hər masaya/sifarişə girəndə "Digər əməliyyatlar" menyusu yığcam (bağlı) açılsın
    useEffect(() => { setMoreActionsOpen(false); }, [currentOrderId]);

    const loadMenu = useCallback(async () => {
        try {
            const res = await api.get('/menu/full?favorites=1');
            setMenu(res.data.data);
            setBanquetEnabled(Number(res.data.banquet_enabled || 0) === 1);
        } catch {
            toast.error('Menyu yüklənmədi.');
        }
        // Dinamik sürətli miqdarlar (hər məhsul üçün ən çox işlənənlər) — səssiz
        try {
            const pq = await api.get('/orders/popular-quantities');
            setPopularQtys(pq.data?.data || {});
        } catch { /* ignore */ }
    }, []);

    // Online qayıdanda (OfflineBanner növbəni sinxronlaşdırandan SONRA 'app-resync' online gəlir):
    // menyu + açıq sifariş serverdən təzələnir. Offline keçiddə heç nə yüklənmir — ekrandakı vəziyyət
    // onsuz da ən təzədir (keşdən təkrar yükləmək köhnəni qaytara bilər).
    const resyncRef = useRef(null);
    resyncRef.current = async () => {
        loadMenu();
        if (currentOrderId && !order?._offline) {
            // offline əlavələr növbəyə keçib (sinxronlaşıb) → lokal state-də qalıb ikiqat görünməsin
            setOffAdds(readOffAdds(currentOrderId));
            setOffPrinted({});
            try { localStorage.removeItem(offPrintedKey(currentOrderId)); } catch { /* ignore */ }
            loadOrder(currentOrderId);
            return;
        }
        if (order?._offline) {
            const d = readOffDraft(tableId);
            if (d && Array.isArray(d.items) && d.items.length) return; // draft hələ lokaldadır
            // Draft sinxron növbəsinə keçib → yeni dəyişiklik ikinci sifariş yaratmasın: serverdəki sifarişə keç
            try {
                const r = await api.get('/tables');
                const rows = (r.data && (r.data.data || r.data.tables)) || [];
                const t = (Array.isArray(rows) ? rows : []).find((x) => String(x.id) === String(tableId));
                if (t?.order_id) { navigate(`/waiter/order/${t.order_id}`, { replace: true }); return; }
            } catch { /* ignore */ }
            toast('İnternet qayıtdı. Offline sifariş sinxronlaşdırılır.');
            navigate('/waiter/tables');
        }
    };
    useEffect(() => {
        const onResync = (e) => { if (e?.detail?.reason === 'online') resyncRef.current?.(); };
        window.addEventListener('app-resync', onResync);
        return () => window.removeEventListener('app-resync', onResync);
    }, []);

    // Maya (cost) düyməsi — açılanda həmişə yenidən çək (məhsul dəyişsə köhnə qalmasın)
    const toggleMaya = async () => {
        const next = !mayaOpen;
        setMayaOpen(next);
        if (!next || !currentOrderId) return;
        setMayaLoading(true);
        try {
            const res = await api.get(`/orders/${currentOrderId}/maya`);
            setMayaData(res.data);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Maya alınmadı.');
            setMayaOpen(false);
        } finally {
            setMayaLoading(false);
        }
    };

    // Sifarişin WhatsApp mesajını qur (məhsullar + qeyd + cəmlər + açılış vaxtı + ofisiant/masa)
    const buildOrderWhatsappMessage = () => {
        const o = order;
        if (!o) return '';
        const items = o.items || [];
        const sub  = items.reduce((s, it) => s + parseFloat(it.unit_price || 0) * Number(it.qty || 0), 0);
        const svcPct = toMoney(o.service_percent);
        const svcAmt = toMoney(o.service_amount);
        const disc   = toMoney(o.discount_amount);
        const dep    = toMoney(o.applied_deposit);
        const grand  = toMoney(o.grand_total || sub + svcAmt - disc + dep);
        const lines = items.map((it) => {
            const name = it._isSetGroup ? `🎁 ${it.item_name}` : it.item_name;
            const qtyLabel = it.qty_label || formatQty(it.qty, it.sale_type);
            const linePrice = (parseFloat(it.unit_price || 0) * Number(it.qty || 0)).toFixed(2);
            let s = `${qtyLabel} × ${name} — ${linePrice} azn`;
            if (it.note) s += `\nqeyd: ${it.note}`;
            return s;
        });
        let msg = `🍽️ ${o.table_name || 'Masa'}\n\n`;
        msg += lines.join('\n');
        msg += `\n\nCəm: ${sub.toFixed(2)} azn`;
        msg += `\n${trimQty(svcPct, 2)}% servis haqqı: ${svcAmt.toFixed(2)} azn`;
        if (dep > 0)  msg += `\nDepozit: ${dep.toFixed(2)} azn`;
        if (disc > 0) msg += `\nEndirim: ${disc.toFixed(2)} azn`;
        msg += `\nÜmumi cəm: ${grand.toFixed(2)} azn`;
        if (o.opened_at) msg += `\n\nTarix: ${fmtDateTimeNum(o.opened_at)}-də açılıb`;
        msg += `\nOfisiant: ${o.waiter_name || '-'} · Masa: ${o.table_name || '-'}`;
        return msg;
    };

    const sendOrderWhatsapp = async () => {
        const num = String(waNum).trim();
        if (!num) return toast.error('Nömrə yazın.');
        if (!currentOrderId) return;
        const message = buildOrderWhatsappMessage();
        setWaSending(true);
        try {
            const res = await api.post(`/orders/${currentOrderId}/whatsapp-send`, { number: num, message });
            toast.success(res.data.msg || 'WhatsApp-a göndərildi.');
            const next = [num, ...waHistory.filter(n => n !== num)].slice(0, 3);
            setWaHistory(next);
            try { localStorage.setItem('wa_recent_nums', JSON.stringify(next)); } catch { /* ignore */ }
            setWaModal(false);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Göndərilmədi.');
        } finally {
            setWaSending(false);
        }
    };

    useEffect(() => {
        const init = async () => {
            if (currentOrderId) {
                await Promise.all([loadOrder(currentOrderId), loadMenu()]);
            } else {
                await loadMenu();
            }

            setLoading(false);
        };

        init();
    }, [currentOrderId, loadMenu, loadOrder]);

    // Məhsul vaxt detalı açıq olanda "X dəq əvvəl"-i hər 60 san yenilə
    useEffect(() => {
        if (timeItemId == null && !showAllTimes) return undefined;
        const t = setInterval(() => setNowTick((n) => n + 1), 60000);
        return () => clearInterval(t);
    }, [timeItemId, showAllTimes]);

    // ── Offline-da MÖVCUD açıq sifarişə əlavələr (pending; serverdəki item-lərə toxunmur) ──
    const mkOffLineItem = (item, qty, overridePrice) => {
        const variantId = item._variantId || null;
        const unitPrice = (overridePrice !== null && overridePrice !== undefined) ? Number(overridePrice) : parseFloat(item.price);
        return {
            id: 'off_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
            item_id: item.id,
            item_name: item._variantName ? `${item.name} (${item._variantName})` : item.name,
            sale_type: item.sale_type || 'piece',
            qty: Number(qty), unit_price: Number(unitPrice), note: null,
            _variantId: variantId, _variantName: item._variantName || null,
            // Server sətrindən gəlirsə onun qeydi (variantlı sətirdə variant adıdır) — birləşmə üçün
            _storedNote: item._storedNote || null,
            kitchen_printed: 0, printed_qty: 0, _pending: true,
        };
    };
    // Menyudan əlavə / təkrar: deltanı YIĞ (eyni məhsul pending-ə əlavə olunur → display-də birləşir)
    const addToExistingOffline = (item, qty, overridePrice = null) => {
        const oid = currentOrderId;
        const adds = [...readOffAdds(oid)];
        const variantId = item._variantId || null;
        const unitPrice = (overridePrice !== null && overridePrice !== undefined) ? Number(overridePrice) : parseFloat(item.price);
        const idx = adds.findIndex((l) =>
            l.item_id === item.id && (l._variantId || null) === variantId &&
            Number(l.unit_price) === Number(unitPrice) && !l.note);
        if (idx >= 0) adds[idx] = { ...adds[idx], qty: Number(adds[idx].qty) + Number(qty) };
        else adds.push(mkOffLineItem(item, qty, overridePrice));
        writeOffAdds(oid, adds); setOffAdds(adds);
    };
    // Server sətrinin MÜTLƏQ sayını təyin et → pending deltanı (newQty − serverQty) yaz/sil (+/- və edit üçün)
    const setExistingDelta = (si, newQty) => {
        const oid = currentOrderId;
        const variantId = si.variant_id || si._variantId || null;
        const base = readOffAdds(oid).filter((a) =>
            !(String(a.item_id) === String(si.item_id) && (a._variantId || null) === variantId &&
              Number(a.unit_price) === Number(si.unit_price) && !a.note));
        const delta = Number(newQty) - Number(si._serverQty != null ? si._serverQty : si.qty);
        if (delta > 0) {
            base.push(mkOffLineItem({
                id: si.item_id, name: si.item_name, price: si.unit_price,
                sale_type: si.sale_type || 'piece', _variantId: variantId, _variantName: null,
                _storedNote: si.note || null,
            }, delta));
        }
        writeOffAdds(oid, base); setOffAdds(base);
    };
    // Mövcud sifarişə offline əlavələri göndər → order.addItems op → Zala qayıt
    const offlineSendAddItems = async () => {
        const oid = currentOrderId;
        const adds = readOffAdds(oid);
        if (!adds.length) return;
        const { items: addsF } = await offlineKitchenFlush(adds);
        const printed = false; // sətir başına printed_qty göndərilir
        enqueueOp('order.addItems', {
            order_id: Number(oid),
            already_printed: printed,
            items: addsF.map((it) => ({
                item_id: it.item_id || null, custom_name: it.custom_name || null, custom_printer: it.custom_printer || null,
                variant_id: it._variantId || it.variant_id || null, qty: Number(it.qty),
                unit_price: Number(it.unit_price), note: it._storedNote || it.note || null, variant_name: it._variantName || null, printed_qty: Number(it.printed_qty || 0),
            })),
        });
        clearOffAdds(oid); setOffAdds([]);
        toast.success('📴 Offline əlavələr göndərildi. İnternet qayıdanda sinxronlaşacaq.', { duration: 4000 });
        navigate('/waiter/tables');
    };
    // Mövcud offline sifariş açılanda saxlanan pending əlavələri göstər
    useEffect(() => {
        if (!currentOrderId) return;
        if (!(offlineEnabled() && graceValidToday() && isOffline())) return;
        const adds = readOffAdds(currentOrderId);
        if (adds.length) setOffAdds(adds);
        setOffPrinted(readOffPrinted(currentOrderId)); // offline-da mətbəxə göndərilmiş online sətirlər
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrderId, netOffline, order?.id]);

    // ── Offline səbət əməliyyatları (hamısı draft üzərində; draft = həqiqət mənbəyi) ──
    const addToOfflineCart = (item, qty, overridePrice = null) => {
        const draft = readOffDraft(tableId) || { items: [], note };
        const items = [...(draft.items || [])];
        const variantId = item._variantId || null;
        const unitPrice = (overridePrice !== null && overridePrice !== undefined) ? Number(overridePrice) : parseFloat(item.price);
        // Eyni məhsul + variant + qiymət + qeydsiz → birləşdir (qty artır)
        const idx = items.findIndex((l) =>
            l.item_id === item.id && (l._variantId || null) === variantId &&
            Number(l.unit_price) === Number(unitPrice) && !l.note);
        if (idx >= 0) {
            items[idx] = { ...items[idx], qty: Number(items[idx].qty) + Number(qty) };
        } else {
            items.push({
                id: 'off_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
                item_id: item.id,
                item_name: item._variantName ? `${item.name} (${item._variantName})` : item.name,
                sale_type: item.sale_type || 'piece',
                qty: Number(qty), unit_price: Number(unitPrice), note: null,
                _variantId: variantId, _variantName: item._variantName || null,
                kitchen_printed: 0, printed_qty: 0,
            });
        }
        persistOfflineCart(items, draft.note ?? note);
    };

    // Offline MƏTBƏX ÇAPI — keşlənmiş menyudan hər məhsulun printer-ini tapıb (server claim-kitchen
    // məntiqi ilə eyni: custom → variant → məhsul → kateqoriya → xp-metbex), printerə görə qruplaşdırıb
    // bridge (C#/LAN) ilə çap edir. İnternet lazım deyil (lokal). Uğurda true → already_printed=true.
    const offlinePrintKitchen = async (items) => {
        try {
            const hasBridge = hasWebViewBridge() || (window.qz && window.qz.websocket?.isActive?.());
            if (!hasBridge) return false; // çap vasitəsi yoxdur → online qayıdanda çap olunacaq
            // menyu indeksi (item_id → {printer, variants, _catPrinter})
            // Virtual kateqoriyalar (Favorilər / Yeniliklər / Setlər) printer daşımır → əsl kateqoriya üstün
            const idx = {};
            for (const cat of (menu || [])) {
                const virtual = typeof cat.id !== 'number' && !/^\d+$/.test(String(cat.id));
                for (const it of (cat.items || [])) {
                    const key = String(it.id);
                    if (!idx[key] || !virtual) idx[key] = { ...it, _catPrinter: virtual ? (idx[key]?._catPrinter || null) : (cat.printer || null) };
                }
            }
            const printerInfo = {};
            for (const p of (kitchenPrinters || [])) printerInfo[p.name] = p;

            // Server (claim-kitchen) ilə EYNİ sıra: xüsusi → variant → məhsul → KATEQORİYA → xp-metbex.
            // Əvvəl kateqoriya addımı yox idi → printer kateqoriyaya təyin olunanda çek mövcud olmayan
            // xp-metbex-ə gedirdi və çıxmırdı.
            const resolvePrinter = (it) => {
                if (it.custom_name) return it.custom_printer || 'none';
                const m = idx[String(it.item_id)];
                if (!m) return 'xp-metbex';
                const vid = it._variantId || it.variant_id;
                if (vid && Array.isArray(m.variants)) {
                    const v = m.variants.find((x) => String(x.id) === String(vid));
                    if (v && v.printer) return v.printer;
                }
                return m.printer || m._catPrinter || 'xp-metbex';
            };

            const groups = {};
            for (const it of (items || [])) {
                if (Number(it.qty) <= 0) continue;
                const printer = resolvePrinter(it);
                if (!printer || printer === 'none') continue;
                if (!groups[printer]) {
                    const info = printerInfo[printer];
                    groups[printer] = {
                        printer,
                        printer_label: info?.label || printer,
                        ip: (info?.conn_type === 'lan') ? (info?.ip || '') : '',
                        items: [],
                    };
                }
                groups[printer].items.push({
                    item_name: it.item_name, qty: Number(it.qty),
                    sale_type: it.sale_type || 'piece', note: it.note || null,
                });
            }
            const groupList = Object.values(groups);
            if (!groupList.length) return false;

            const synthetic = { table_name: offlineTableName(), waiter_name: user?.name || '', id: null, opened_at: null };
            let anyOk = false;
            for (const g of groupList) {
                try {
                    await printKitchen(g.printer, buildKitchenTicketText(synthetic, g, kitchenQtyX), g.ip);
                    anyOk = true;
                } catch (e) { console.error('[offline kitchen]', g.printer, e?.message); }
            }
            return anyOk;
        } catch (e) { console.error('[offlinePrintKitchen]', e?.message); return false; }
    };

    // Yalnız mətbəxə HƏLƏ getməmiş hissəni çap et (qty − printed_qty) → uğurda hamısı «göndərilib».
    // Beləcə «Mətbəxə göndər»dən sonra təsdiq/bağlama zamanı təkrar çap olmur; serverə sətir başına
    // printed_qty gedir (online qayıdanda da təkrar çap olunmur).
    const offlineKitchenFlush = async (items) => {
        const unsent = (items || [])
            .map((it) => ({ ...it, qty: Number(it.qty) - Number(it.printed_qty || 0) }))
            .filter((it) => it.qty > 0);
        if (!unsent.length) return { items: items || [], sent: false, nothing: true };
        const ok = await offlinePrintKitchen(unsent);
        if (!ok) return { items: items || [], sent: false };
        return { items: (items || []).map((it) => ({ ...it, printed_qty: Number(it.qty), kitchen_printed: 1 })), sent: true };
    };
    const offPrintedKey = (oid) => `pos_offline_queue_printed:${oid}`; // C# açılışda silinməyən prefiks
    const readOffPrinted = (oid) => { try { return JSON.parse(localStorage.getItem(offPrintedKey(oid)) || '{}'); } catch { return {}; } };
    const writeOffPrinted = (oid, m) => { try { localStorage.setItem(offPrintedKey(oid), JSON.stringify(m)); } catch { /* ignore */ } };
    // Online vurulub mətbəxə hələ getməmiş server sətirləri (offline-da göndərilənlər çıxılmaqla)
    const serverUnsentRows = () => {
        if (order?._offline || !currentOrderId) return [];
        const op = readOffPrinted(currentOrderId);
        return (order?.items || [])
            .filter((r) => !r.set_group_id)
            .map((r) => ({ r, done: Math.max(Number(r.printed_qty || 0), Number(op[r.id] || 0)) }))
            .filter(({ r, done }) => Number(r.qty) > done)
            .map(({ r, done }) => ({ ...r, _variantId: r.variant_id || null, qty: Number(r.qty) - done }));
    };
    const offlineUnsentCount = () => {
        if (order?._offline) return (order?.items || []).filter((it) => Number(it.qty) > Number(it.printed_qty || 0)).length;
        return (offAdds || []).filter((it) => Number(it.qty) > Number(it.printed_qty || 0)).length + serverUnsentRows().length;
    };

    // 🔥 Mətbəxə göndər (offline) — internet yoxdur, çap lokal (C# / LAN)
    const offlineSendKitchen = async () => {
        if (printing) return;
        setPrinting(true);
        try {
            if (order?._offline) {
                const draft = readOffDraft(tableId);
                const r = await offlineKitchenFlush(draft?.items || []);
                if (r.nothing) { toast('Bütün məhsullar artıq mətbəxə göndərilib.'); return; }
                if (!r.sent) { toast.error('Mətbəx printeri cavab vermədi.'); return; }
                persistOfflineCart(r.items, draft?.note ?? note);
            } else if (currentOrderId) {
                const oid = currentOrderId;
                const adds = readOffAdds(oid);
                const addsUnsent = adds.map((it) => ({ ...it, qty: Number(it.qty) - Number(it.printed_qty || 0) })).filter((it) => it.qty > 0);
                const rows = serverUnsentRows();
                if (!addsUnsent.length && !rows.length) { toast('Bütün məhsullar artıq mətbəxə göndərilib.'); return; }
                const ok = await offlinePrintKitchen([...rows, ...addsUnsent]);
                if (!ok) { toast.error('Mətbəx printeri cavab vermədi.'); return; }
                const addsDone = adds.map((it) => ({ ...it, printed_qty: Number(it.qty), kitchen_printed: 1 }));
                writeOffAdds(oid, addsDone); setOffAdds(addsDone);
                if (rows.length) {
                    // Online sətirlər: lokal işarə + serverə (sinxronda) printed_qty yaz
                    const m = readOffPrinted(oid);
                    const sent = [];
                    for (const r of rows) {
                        const full = (order?.items || []).find((x) => x.id === r.id);
                        if (!full) continue;
                        m[r.id] = Number(full.qty);
                        sent.push({ id: r.id, printed_qty: Number(full.qty) });
                    }
                    writeOffPrinted(oid, m); setOffPrinted({ ...m });
                    if (sent.length) enqueueOp('order.markPrinted', { order_id: Number(oid), rows: sent });
                }
            }
            toast.success('🔥 Mətbəxə göndərildi (offline).');
        } finally {
            setPrinting(false);
        }
    };

    // 🖨️ Müştəri çeki (offline) — cəm lokal hesablanır (server sinxronda yenidən hesablayır)
    // Online kimi: müştəri çeki çıxdı → bill_printed (kilid + masa «hesab»). Draft-da bayraq, mövcud sifarişdə op.
    const markOfflineBillPrinted = () => {
        if (order?._offline) {
            const d = readOffDraft(tableId);
            if (d) writeOffDraft(tableId, { ...d, bill_printed: 1 });
        } else if (currentOrderId) {
            enqueueOp('order.markBillPrinted', { order_id: Number(currentOrderId), actor_id: user?.id ?? null, actor_name: user?.name || null });
        }
        setOrder((prev) => (prev ? { ...prev, bill_printed: 1 } : prev));
    };

    const offlinePrintBill = async () => {
        const items = offlineCloseItems();
        if (!items.length) { toast.error('Əvvəlcə məhsul əlavə edin.'); return; }
        if (printing) return;
        setPrinting(true);
        try {
            const fin = offlineFinancials(items, offPayDiscount(), !order?._offline && Number(order?.service_exempt || 0) === 1);
            const receiptPrinter = await getReceiptPrinterName();
            await printBill(receiptPrinter, {
                title: getReceiptTitle(user),
                receiptLogoUrl: getReceiptLogoUrl(user),
                phone: order?.tenant_phone || '',
                orderId: order?._offline ? null : order?.id,
                dailyNumber: order?.daily_number || null,
                tableName: order?.table_name || offlineTableName(),
                waiterName: order?.waiter_name || user?.name || '',
                subtotal: fin.subtotal,
                servicePercent: fin.service_percent,
                serviceAmount: fin.service_amount,
                discountAmount: fin.discount,
                depositAmount: 0,
                appliedDeposit: 0,
                grandTotal: fin.grand_total,
                total: fin.grand_total,
                note: order?.note || null,
                items: items.map((it) => ({
                    name: it.item_name,
                    qty: Number(it.qty),
                    qtyLabel: formatQty(it.qty, it.sale_type),
                    unitPrice: Number(it.unit_price),
                    saleType: it.sale_type || 'piece',
                    note: it.note || null,
                    total: Number(it.unit_price) * Number(it.qty),
                })),
            });
            toast.success('Müştəri çeki printerə göndərildi.');
            markOfflineBillPrinted();
        } catch (err) {
            toast.error(err?.message || 'Çap baş tutmadı.');
        } finally {
            setPrinting(false);
        }
    };

    // Offline sifarişi TƏSDİQLƏ → sync növbəsinə (idempotent op) → Zala qayıt.
    // Online qayıdanda avtomatik yüklənir; server item-ləri yaradır (servis%/yekun/ödəniş online hesablanır).
    const confirmOfflineOrder = async () => {
        if (currentOrderId) return; // yalnız yeni offline sifariş
        const draft = readOffDraft(tableId);
        const items = draft?.items || []; // yalnız draft (ikiqat-klik: draft silinibsə no-op)
        if (!items.length) return;
        const { items: itemsF } = await offlineKitchenFlush(items); // yalnız göndərilməmiş hissə
        const printed = false;
        const payload = {
            table_id: Number(tableId),
            waiter_id: (selectedWaiterId ? Number(selectedWaiterId) : null) || draft?.waiter_id || (user?.id ? Number(user.id) : null),
            note: (draft?.note ?? note) || null,
            already_printed: printed, // çap olundusa online qayıdanda təkrar çap olunmur
            bill_printed: draft?.bill_printed ? 1 : 0,
            actor_id: user?.id ?? null, actor_name: user?.name || null,
            items: itemsF.map((it) => ({
                item_id: it.item_id || null,
                custom_name: it.custom_name || null,
                custom_printer: it.custom_printer || null,
                variant_id: it._variantId || it.variant_id || null,
                qty: Number(it.qty),
                unit_price: Number(it.unit_price),
                note: it._storedNote || it.note || null, variant_name: it._variantName || null, printed_qty: Number(it.printed_qty || 0),
            })),
        };
        // _draft: masa yenidən açılanda redaktə üçün geri yüklənir (serverə göndərilmir)
        enqueueOp('order.createComplete', { ...payload, _draft: { ...draft, items: itemsF } });
        clearOffDraft(tableId);
        toast.success('📴 Offline təsdiqləndi. İnternet qayıdanda avtomatik sinxronlaşacaq.', { duration: 4000 });
        navigate('/waiter/tables');
    };

    // Offline hesab cəmi (serverdəki calcFinancials ilə eyni formula; kassa serverdə autoritar).
    // Draft-da endirim/depozit yoxdur → servis %-i tətbiq olunur (minimum həddən yuxarıdırsa).
    // Offline hesab cəmi (server sinxronda YENİDƏN hesablayır — kassa autoritar serverdədir)
    const offlineFinancials = (items, discount = 0, serviceExempt = false) => {
        const sub = Math.round((items || []).reduce((s, it) => s + Number(it.unit_price || 0) * Number(it.qty || 0), 0) * 100) / 100;
        const s = getOfflineSettings();
        const minAmt = Math.max(0, Number(s.service_min_amount) || 0);
        const belowMin = minAmt > 0 && sub <= minAmt;
        const pct = (belowMin || serviceExempt) ? 0 : (Number(s.service_percent) || 0);
        const svc = Math.round(sub * (pct / 100) * 100) / 100;
        const disc = Math.min(Math.max(0, Math.round((Number(discount) || 0) * 100) / 100), sub);
        const grand = Math.max(0, Math.round((sub + svc - disc) * 100) / 100);
        return { subtotal: sub, service_percent: pct, service_amount: svc, discount: disc, grand_total: grand };
    };
    // Bağlanacaq sətirlər: yeni offline sifariş → draft; online açılmış → server sətirləri + offline əlavələr
    const offlineCloseItems = () => {
        if (order?._offline) return readOffDraft(tableId)?.items || order?.items || [];
        return [...(order?.items || []), ...(readOffAdds(currentOrderId) || [])];
    };
    const offPayDiscount = () => {
        const v = String(offPayModal.discount ?? '').replace(',', '.').trim();
        if (v !== '') return Math.max(0, Number(v) || 0);
        return order?._offline ? 0 : Number(order?.discount_amount || 0);
    };

    // Modalda göstərilən yekun (bölünmüşdə kart = yekun − nağd)
    const offPayTotal = () => offlineFinancials(
        offlineCloseItems(), offPayDiscount(), !order?._offline && Number(order?.service_exempt || 0) === 1
    ).grand_total;

    const openOfflineClose = async () => {
        if (!canCloseTable) { toast.error('Hesabı bağlamaq icazəniz yoxdur.'); return; }
        if (getOfflineAuth()?.sayim_active) {
            const isAdm = ['admin', 'superadmin'].includes(user?.role) || String(user?.display_role || '').toLowerCase() === 'admin';
            if (!isAdm) { toast.error('Sayım bağlanmayıb. Hesab yalnız admin tərəfindən bağlana bilər.'); return; }
            if (!(await confirm('Sayım bağlanmayıb. Yenə də təsdiq edək?'))) return;
        }
        const items = offlineCloseItems();
        if (!items.length) { toast.error('Səbət boşdur.'); return; }
        setOffPayModal({ open: true, method: offDeliveryOnly ? 'delivery' : 'nagd', cash: '', card: '', discount: '', cardConfirmed: false });
    };

    // ONLINE açılmış sifarişi offline-da bağla → order.payExisting (əlavələr + ödəniş bir op-da)
    const confirmOfflineCloseExisting = async () => {
        const oid = currentOrderId;
        if (!oid || order?._offline) return;
        const adds = readOffAdds(oid);
        const items = offlineCloseItems();
        const fin = offlineFinancials(items, offPayDiscount(), Number(order?.service_exempt || 0) === 1);
        const method = offPayModal.method;
        let cash = 0, card = 0;
        if (method === 'hesab') {
            cash = Math.min(fin.grand_total, Math.max(0, Number(String(offPayModal.cash).replace(',', '.')) || 0));
            card = Math.max(0, Math.round((fin.grand_total - cash) * 100) / 100); // qalan avtomatik kart
        }
        const hasCard = method === 'kart' || (method === 'hesab' && card > 0);
        if (hasCard && !offPayModal.cardConfirmed) { toast.error('Kartın terminalda çəkildiyini təsdiqləyin.'); return; }
        const { items: addsF } = await offlineKitchenFlush(adds);
        const printed = false;
        const discountTouched = String(offPayModal.discount ?? '').trim() !== '';
        enqueueOp('order.payExisting', {
            order_id: Number(oid),
            table_id: order?.table_id != null ? Number(order.table_id) : null,
            cashier_id: user?.id && /^\d+$/.test(String(user.id)) ? Number(user.id) : null,
            actor_id: user?.id ?? null, // server icazəni yoxlayır (online ilə eyni qayda)
            already_printed: printed,
            method,
            cash_amount: method === 'hesab' ? cash : 0,
            card_amount: method === 'hesab' ? card : 0,
            ...(discountTouched ? { discount_amount: fin.discount } : {}),
            items: addsF.map((it) => ({
                item_id: it.item_id || null, custom_name: it.custom_name || null, custom_printer: it.custom_printer || null,
                variant_id: it._variantId || it.variant_id || null, qty: Number(it.qty),
                unit_price: Number(it.unit_price), note: it._storedNote || it.note || null, variant_name: it._variantName || null, printed_qty: Number(it.printed_qty || 0),
            })),
        });
        clearOffAdds(oid); setOffAdds([]);
        setOffPayModal({ open: false, method: 'nagd', cash: '', card: '' });
        toast.success('📴 Offline bağlandı. Kassa və fiskal internet qayıdanda tamamlanacaq.', { duration: 4000 });
        navigate('/waiter/tables');
    };

    // Offline hesabı bağla → createAndPay op (yaradılır+ödənilir+bağlanır). Fiskal online qayıdanda.
    const confirmOfflineClose = async () => {
        if (currentOrderId) return confirmOfflineCloseExisting(); // online açılmış sifariş
        const draft = readOffDraft(tableId);
        const items = draft?.items || []; // yalnız draft (ikiqat-klik: draft silinibsə no-op)
        if (!items.length) { setOffPayModal({ open: false, method: 'nagd', cash: '', card: '' }); return; }
        const fin = offlineFinancials(items, offPayDiscount());
        const method = offPayModal.method;
        let cash = 0, card = 0;
        if (method === 'hesab') {
            cash = Math.min(fin.grand_total, Math.max(0, Number(String(offPayModal.cash).replace(',', '.')) || 0));
            card = Math.max(0, Math.round((fin.grand_total - cash) * 100) / 100); // qalan avtomatik kart
        }
        const hasCard = method === 'kart' || (method === 'hesab' && card > 0);
        if (hasCard && !offPayModal.cardConfirmed) { toast.error('Kartın terminalda çəkildiyini təsdiqləyin.'); return; }
        const { items: itemsF } = await offlineKitchenFlush(items);
        const printed = false;
        const payload = {
            table_id: Number(tableId),
            waiter_id: (selectedWaiterId ? Number(selectedWaiterId) : null) || draft?.waiter_id || (user?.id ? Number(user.id) : null),
            note: (draft?.note ?? note) || null,
            already_printed: printed,
            actor_id: user?.id ?? null, // server icazəni yoxlayır (online ilə eyni qayda)
            method,
            cash_amount: method === 'hesab' ? cash : 0,
            card_amount: method === 'hesab' ? card : 0,
            discount_amount: fin.discount,
            items: itemsF.map((it) => ({
                item_id: it.item_id || null,
                custom_name: it.custom_name || null,
                custom_printer: it.custom_printer || null,
                variant_id: it._variantId || it.variant_id || null,
                qty: Number(it.qty),
                unit_price: Number(it.unit_price),
                note: it._storedNote || it.note || null, variant_name: it._variantName || null, printed_qty: Number(it.printed_qty || 0),
            })),
        };
        enqueueOp('order.createAndPay', payload);
        clearOffDraft(tableId);
        setOffPayModal({ open: false, method: 'nagd', cash: '', card: '' });
        toast.success('📴 Offline bağlandı. Fiskal və kassa internet qayıdanda tamamlanacaq.', { duration: 4000 });
        navigate('/waiter/tables');
    };

    const ensureOrder = async () => {
        if (order?._offline) return 'offline'; // offline: server sifariş yaradılmır (draft istifadə olunur)
        if (currentOrderId) {
            return currentOrderId;
        }
        if (viewOnly) { toast.error(VIEW_ONLY_MSG); throw new Error(VIEW_ONLY_MSG); }

        const payload = { table_id: tableId };
        if (selectedWaiterId) payload.waiter_id = Number(selectedWaiterId);
        try {
            const res = await api.post('/orders', payload);
            const oid = res.data.id;
            setCurrentOrderId(oid);
            window.history.replaceState(window.history.state, '', `/waiter/order/${oid}`);
            return oid;
        } catch (err) {
            // Masa artıq başqa ofisiant tərəfindən idarə olunur → mesaj + masalar siyahısına qayıt
            if (err.response?.status === 403 && err.response?.data?.locked) {
                toast.error(err.response.data.msg || 'Bu masa artıq başqa ofisiant tərəfindən idarə olunur.');
                navigate('/waiter/tables');
                return null;
            }
            throw err;
        }
    };

    const submitItemToOrder = async (item, qty, overridePrice = null) => {
        if (order?._offline) {
            if (item.is_set) { toast.error('Offline rejimdə set əlavə edilə bilməz.'); return; }
            addToOfflineCart(item, qty, overridePrice);
            const label = item._variantName ? `${item.name} (${item._variantName})` : item.name;
            toast.success(`${label} əlavə edildi.`);
            return;
        }
        if (offlineExistingActive()) {
            if (item.is_set) { toast.error('Offline rejimdə set əlavə edilə bilməz.'); return; }
            addToExistingOffline(item, qty, overridePrice);
            const label = item._variantName ? `${item.name} (${item._variantName})` : item.name;
            toast.success(`${label} əlavə edildi (offline).`);
            return;
        }
        const oid = await ensureOrder();
        if (!oid) return; // masa bloklandı → geri çıxıldı

        if (item.is_set) {
            await api.post(`/orders/${oid}/sets`, {
                set_id: item.set_id,
                qty
            });
        } else {
            const payload = { item_id: item.id, qty };
            if (item._variantName) {
                payload.unit_price = parseFloat(item.price);
                payload.variant_name = item._variantName;
                if (item._variantId) payload.variant_id = item._variantId;
            }
            if (overridePrice !== null && overridePrice !== undefined) {
                payload.unit_price = Number(overridePrice);
            }
            await api.post(`/orders/${oid}/items`, payload);
        }

        await loadOrder(oid);
        const label = item._variantName ? `${item.name} (${item._variantName})` : item.name;
        toast.success(`${label} əlavə edildi.`);
    };

    // Sürətli miqdar düymələri: bu məhsul üçün ən çox işlənənlər (varsa), yoxsa standart
    const quickValuesFor = (menuItemId, saleType) => {
        const pop = popularQtys[String(menuItemId)];
        if (Array.isArray(pop) && pop.length > 0) return pop;
        return getQuickValues(saleType);
    };

    const openQtyModalForAdd = (item) => {
        const presets = quickValuesFor(item.id, item.sale_type);
        setQtyModal({
            open: true,
            mode: 'add',
            saleType: item.sale_type || 'weight',
            item,
            orderItem: null,
            value: presets[1] || presets[0] || '1',
            quickValues: presets,
            fresh: true
        });
    };

    const openQtyModalForEdit = (orderItem) => {
        const st = orderItem.sale_type || 'weight';
        const isPiece = st !== 'weight' && st !== 'liter';
        // Ədəd → tam rəqəm (40, "40.000" yox); çəki/litr → ondalıqları saxla
        const val = isPiece ? String(Math.round(Number(orderItem.qty) || 0)) : String(orderItem.qty);
        setQtyModal({
            open: true,
            mode: 'edit',
            saleType: st,
            item: null,
            orderItem,
            value: val,
            quickValues: quickValuesFor(orderItem.item_id, st),
            fresh: true
        });
    };

    const openQtyModalForRepeat = (orderItem) => {
        const presets = quickValuesFor(orderItem.item_id, orderItem.sale_type);
        setQtyModal({
            open: true,
            mode: 'repeat',
            saleType: orderItem.sale_type || 'weight',
            item: null,
            orderItem,
            value: presets[1] || presets[0] || '1',
            quickValues: presets,
            fresh: true
        });
    };

    const closeQtyModal = () => {
        setQtyModal({
            open: false,
            mode: 'add',
            saleType: 'weight',
            item: null,
            orderItem: null,
            value: ''
        });
    };

    const confirmQtyModal = async () => {
        const parsedQty = Number(String(qtyModal.value).replace(',', '.'));
        if (!Number.isFinite(parsedQty) || parsedQty <= 0) {
            toast.error('Düzgün miqdar daxil edin.');
            return;
        }

        try {
            if (qtyModal.mode === 'add' && qtyModal.item) {
                await submitItemToOrder(qtyModal.item, parsedQty);
            }

            if (qtyModal.mode === 'edit' && qtyModal.orderItem) {
                await updateQty(qtyModal.orderItem.id, parsedQty);
            }

            if (qtyModal.mode === 'repeat' && qtyModal.orderItem) {
                await appendExistingOrderItem(qtyModal.orderItem, parsedQty);
            }

            closeQtyModal();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    // App-ın öz rəqəm keypadı (OS klaviaturası açılmasın)
    const qtyKeypadPress = (key) => {
        setQtyModal((prev) => {
            const isPiece = prev.saleType !== 'weight' && prev.saleType !== 'liter';
            // Açılışdakı dəyər "seçili"dir: ilk rəqəm onu əvəz edir (1 → 20, "120" yox),
            // sonrakı rəqəmlər əlavə olunur (21, 45).
            let v = prev.fresh ? '' : String(prev.value ?? '');
            let fresh = false;
            if (key === 'C') v = '';
            else if (key === 'back') v = v.slice(0, -1);
            else if (key === '.') { if (!isPiece && !v.includes('.')) v = (v === '' ? '0' : v) + '.'; } // ədəddə ondalıq yoxdur
            else { v = (v === '0') ? key : v + key; } // rəqəm — baş sıfırı əvəz et
            return { ...prev, value: v, fresh };
        });
    };

    // variantOverride: null = not yet chosen (show picker), false = explicitly no variant (use default), object = use variant
    const addItem = async (item, variantOverride = null) => {
        if (viewOnly) { toast.error(VIEW_ONLY_MSG); return; }
        try {
            // Sadə satış məhsulu — kassir qiymət yazsın
            if (variantOverride === null && Number(item.is_open_price || 0) === 1) {
                setOpenPriceModal({ open: true, item, price: '' });
                return;
            }

            // If item has variants and no selection has been made yet, show the picker
            if (variantOverride === null && item.variants?.length > 0) {
                setVariantModal({ open: true, item });
                return;
            }

            const effectiveItem = (variantOverride && typeof variantOverride === 'object')
                ? { ...item, price: variantOverride.price, _variantName: variantOverride.name, _variantId: variantOverride.id }
                : item;

            if (effectiveItem.is_set || (effectiveItem.sale_type || 'piece') === 'piece') {
                await submitItemToOrder(effectiveItem, 1);
                return;
            }

            openQtyModalForAdd(effectiveItem);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Əlavə edilmədi.');
        }
    };

    const appendExistingOrderItem = async (orderItem, qty = 1) => {
        if (order?._offline) {
            const draft = readOffDraft(tableId) || { items: [], note };
            const items = [...(draft.items || [])];
            const idx = items.findIndex((l) => l.id === orderItem.id);
            if (idx >= 0) {
                items[idx] = { ...items[idx], qty: Number(items[idx].qty) + Number(qty) };
            } else {
                items.push({
                    id: 'off_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
                    item_id: orderItem.item_id, item_name: orderItem.item_name,
                    sale_type: orderItem.sale_type || 'piece',
                    qty: Number(qty), unit_price: Number(orderItem.unit_price || 0),
                    note: orderItem.note || null,
                    _variantId: orderItem._variantId || orderItem.variant_id || null,
                    _variantName: orderItem._variantName || null,
                    kitchen_printed: 0, printed_qty: 0,
                });
            }
            persistOfflineCart(items, draft.note ?? note);
            toast.success(`${orderItem.item_name} əlavə edildi.`);
            return;
        }
        if (offlineExistingActive()) {
            addToExistingOffline({
                id: orderItem.item_id, name: orderItem.item_name, price: orderItem.unit_price || 0,
                sale_type: orderItem.sale_type || 'piece',
                _variantId: orderItem._variantId || orderItem.variant_id || null,
                _variantName: orderItem._variantName || null,
                _storedNote: orderItem.note || null,
            }, qty);
            toast.success(`${orderItem.item_name} əlavə edildi (offline).`);
            return;
        }
        try {
            const oid = await ensureOrder();
            if (!oid) return; // masa bloklandı → geri çıxıldı
            await api.post(`/orders/${oid}/items`, {
                item_id: orderItem.item_id,
                qty,
                note: orderItem.note || null
            });
            await loadOrder(oid);
            toast.success(`${orderItem.item_name} əlavə edildi.`);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Əlavə edilmədi.');
        }
    };

    const updateQty = async (oiId, qty) => {
        if (viewOnly) { toast.error(VIEW_ONLY_MSG); return; }
        if (order?._offline) {
            const draft = readOffDraft(tableId); if (!draft) return;
            const items = (draft.items || []).map((l) => (l.id === oiId ? { ...l, qty: Number(qty) } : l));
            persistOfflineCart(items, draft.note ?? note);
            return;
        }
        if (offlineExistingActive()) {
            // Pending (yeni, server-də olmayan) sətir → sərbəst dəyiş
            if (String(oiId).startsWith('off_')) {
                const adds = readOffAdds(currentOrderId).map((l) => (l.id === oiId ? { ...l, qty: Number(qty) } : l));
                writeOffAdds(currentOrderId, adds); setOffAdds(adds);
                return;
            }
            // Server məhsulu → mütləq say təyin et (eyni sətir üstündə). Server sayından aşağı olmaz.
            const si = (order?.items || []).find((it) => String(it.id) === String(oiId));
            if (!si) return;
            if (Number(qty) < Number(si.qty || 0)) {
                toast.error('Offline rejimdə mövcud məhsul server sayından aşağı azaldıla bilməz.');
                return;
            }
            setExistingDelta(si, Number(qty)); // delta = qty − serverQty (artım), 0 olsa pending silinir
            return;
        }
        if (!currentOrderId) return;

        try {
            await api.put(`/orders/${currentOrderId}/items/${oiId}`, { qty });
            await loadOrder(currentOrderId);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const removeItem = async (oiId) => {
        if (order?._offline) {
            const draft = readOffDraft(tableId); if (!draft) return;
            const items = (draft.items || []).filter((l) => l.id !== oiId);
            persistOfflineCart(items, draft.note ?? note);
            return;
        }
        if (offlineExistingActive()) {
            // Yeni (server-də olmayan) pending sətir → tam sil
            if (String(oiId).startsWith('off_')) {
                const adds = readOffAdds(currentOrderId).filter((l) => l.id !== oiId);
                writeOffAdds(currentOrderId, adds); setOffAdds(adds);
                return;
            }
            // Server sətri → yalnız offline əlavəsini (deltanı) geri al (server məhsulu silinmir)
            const si = (order?.items || []).find((it) => String(it.id) === String(oiId));
            if (!si) return;
            const variantId = si.variant_id || null;
            const before = readOffAdds(currentOrderId);
            const after = before.filter((a) =>
                !(String(a.item_id) === String(si.item_id) && (a._variantId || null) === variantId &&
                  Number(a.unit_price) === Number(si.unit_price) && !a.note));
            if (after.length !== before.length) {
                writeOffAdds(currentOrderId, after); setOffAdds(after);
                toast.success('Offline əlavə geri alındı.');
            } else {
                toast.error('Offline rejimdə mövcud məhsul silinə bilməz. Yalnız offline əlavələri silmək olar.');
            }
            return;
        }
        if (!currentOrderId) return;

        try {
            await api.delete(`/orders/${currentOrderId}/items/${oiId}`);
            await loadOrder(currentOrderId);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    // ── Set qrupu əməliyyatları ────────────────────────────────────────────────
    const addSetGroup = async (setId) => {
        if (viewOnly) { toast.error(VIEW_ONLY_MSG); return; }
        if (order?._offline) { toast.error('Offline rejimdə set əməliyyatı mövcud deyil.'); return; }
        if (!currentOrderId || !setId) return;
        try {
            await api.post(`/orders/${currentOrderId}/sets`, { set_id: setId, qty: 1 });
            await loadOrder(currentOrderId);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Set əlavə olunmadı.');
        }
    };
    const decrementSetGroup = async (groupId) => {
        if (!currentOrderId || !groupId) return;
        if (offlineExistingActive()) { toast.error('Offline rejimdə set dəyişdirilə bilməz. İnternet qayıdanda edin.'); return; }
        try {
            await api.post(`/orders/${currentOrderId}/set-groups/${groupId}/decrement`);
            await loadOrder(currentOrderId);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Azaldılmadı.');
        }
    };
    const deleteSetGroup = async (groupId) => {
        if (!currentOrderId || !groupId) return;
        if (offlineExistingActive()) { toast.error('Offline rejimdə set silinə bilməz. İnternet qayıdanda edin.'); return; }
        try {
            await api.delete(`/orders/${currentOrderId}/set-groups/${groupId}`);
            await loadOrder(currentOrderId);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    const openNoteModal = (orderItem) => {
        setNoteItem(orderItem);
        setItemNote(orderItem.note || '');
        setNoteModal(true);
    };

    // Offline sətrin qeydi: variantlıda online formatı («Variant — qeyd») da yenilənsin
    const withOffNote = (l, val) => ({
        ...l, note: val || null,
        _storedNote: l._variantName ? [l._variantName, val].filter(Boolean).join(' — ') : null,
    });

    const saveItemNote = async () => {
        if (order?._offline) {
            const draft = readOffDraft(tableId); if (!draft || !noteItem) return;
            const items = (draft.items || []).map((l) => (l.id === noteItem.id ? withOffNote(l, itemNote) : l));
            persistOfflineCart(items, draft.note ?? note);
            toast.success('Qeyd yadda saxlandı.');
            setNoteModal(false);
            return;
        }
        if (offlineExistingActive() && noteItem) {
            if (String(noteItem.id).startsWith('off_')) {
                // Offline əlavə (hələ serverdə yox) → lokal sətir
                const adds = readOffAdds(currentOrderId).map((l) => (l.id === noteItem.id ? withOffNote(l, itemNote) : l));
                writeOffAdds(currentOrderId, adds); setOffAdds(adds);
            } else {
                // Server sətri → online ilə eyni kilid, sinxronda tətbiq olunur
                if (!canOverridePrintLock && (Number(noteItem.kitchen_printed || 0) === 1 || Number(order?.bill_printed || 0) === 1)) {
                    toast.error('Bu məhsul mətbəxə göndərilib. Onu yalnız admin dəyişə bilər.');
                    return;
                }
                enqueueOp('order.setItemNote', { order_id: Number(currentOrderId), row_id: Number(noteItem.id), note: itemNote || null, actor_id: user?.id ?? null });
                setOrder((prev) => (prev ? { ...prev, items: (prev.items || []).map((it) => (it.id === noteItem.id ? { ...it, note: itemNote || null } : it)) } : prev));
            }
            toast.success('Qeyd yadda saxlandı.');
            setNoteModal(false);
            return;
        }
        if (!currentOrderId || !noteItem) return;

        try {
            await api.put(`/orders/${currentOrderId}/items/${noteItem.id}/note`, {
                note: itemNote || null
            });
            await loadOrder(currentOrderId);
            toast.success('Qeyd yadda saxlandı.');
            setNoteModal(false);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const saveNote = async () => {
        if (order?._offline) {
            const draft = readOffDraft(tableId) || { items: [] };
            persistOfflineCart(draft.items || [], note);
            toast.success('Qeyd yadda saxlandı.');
            setShowNote(false);
            return;
        }
        if (offlineExistingActive()) {
            enqueueOp('order.setNote', { order_id: Number(currentOrderId), note: note || null, actor_id: user?.id ?? null });
            setOrder((prev) => (prev ? { ...prev, note } : prev));
            toast.success('Qeyd yadda saxlandı. İnternet qayıdanda sinxronlaşacaq.');
            setShowNote(false);
            return;
        }
        if (!currentOrderId) return;

        try {
            await api.put(`/orders/${currentOrderId}/note`, { note });
            // Yerli order obyektini dərhal yenilə → qeyd yuxarıda dərhal görünsün
            // (masadan çıxıb girməyə ehtiyac qalmasın)
            setOrder(prev => (prev ? { ...prev, note } : prev));
            toast.success('Qeyd yadda saxlandı.');
            setShowNote(false);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const cancelOrder = async () => {
        if (!currentOrderId) {
            navigate('/waiter/tables');
            return;
        }

        try {
            await api.post(`/orders/${currentOrderId}/cancel`);
            toast.success('Sifariş ləğv edildi.');
            setCancelModal(false);
            navigate('/waiter/tables');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const openMoveModal = async () => {
        if (!currentOrderId) {
            toast.error('Əvvəlcə sifariş açılmalıdır.');
            return;
        }

        try {
            const res = await api.get(`/orders/${currentOrderId}/movable-tables`);
            setMoveTargets(res.data.data || []);
            setSelectedMoveTableId(String(res.data.data?.[0]?.id || ''));
            setMoveSearch('');
            setMoveMode('move');
            setMergeTargets([]);
            setSelectedMergeOrderId('');
            setMoveModal(true);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Boş masalar yüklənmədi.');
        }
    };

    const moveOrderToTable = async () => {
        if (!currentOrderId || !selectedMoveTableId) {
            toast.error('Masa seçin.');
            return;
        }

        setMovingOrder(true);
        try {
            await api.post(`/orders/${currentOrderId}/move`, {
                target_table_id: Number(selectedMoveTableId)
            });
            await loadOrder(currentOrderId);
            setMoveModal(false);
            toast.success('Sifariş yeni masaya köçürüldü.');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Masa köçürülmədi.');
        } finally {
            setMovingOrder(false);
        }
    };

    const switchMoveMode = async (mode) => {
        setMoveMode(mode);
        setMoveSearch('');
        if (mode !== 'merge' || !currentOrderId) return;
        setMergeLoading(true);
        try {
            const res = await api.get(`/orders/${currentOrderId}/mergeable-tables`);
            const rows = res.data.data || [];
            setMergeTargets(rows);
            setSelectedMergeOrderId(String(rows[0]?.order_id || ''));
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Dolu masalar yüklənmədi.');
        } finally {
            setMergeLoading(false);
        }
    };

    const mergeOrderIntoTable = async () => {
        const target = mergeTargets.find((t) => String(t.order_id) === String(selectedMergeOrderId));
        if (!currentOrderId || !target) {
            toast.error('Masa seçin.');
            return;
        }
        setMovingOrder(true);
        try {
            const res = await api.post(`/orders/${currentOrderId}/merge`, { target_order_id: Number(target.order_id) });
            setMoveModal(false);
            toast.success(res.data?.msg || 'Masalar birləşdirildi.');
            // Birləşdirilmiş (hədəf) sifarişə keç: eyni komponent qalır, ona görə id-ni özümüz yeniləyirik
            const tid = String(res.data?.target_order_id || target.order_id);
            setCurrentOrderId(tid);
            navigate(`/waiter/order/${tid}`, { replace: true });
            await loadOrder(tid);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Birləşdirilmədi.');
        } finally {
            setMovingOrder(false);
        }
    };

    // Kassa POS terminala qoşuludurmu — ofisiant "POS-a göndər" düyməsinin görünməsi üçün
    useEffect(() => {
        let alive = true;
        api.get('/orders/pos-terminal')
            .then((r) => { if (alive) setPosTerminal(!!r.data?.pos_terminal); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    // Mətbəx çekində miqdar formatı ("əd" vs "X") — superadmin tənzimləyir
    useEffect(() => {
        let alive = true;
        getKitchenQtyX().then((v) => { if (alive) setKitchenQtyX(!!v); }).catch(() => {});
        getKitchenConfirmExit().then((v) => { if (alive) setConfirmKitchenExit(!!v); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    // Birbaşa POS-a göndər (kart) — mərhələli, BLOKLAYAN popup + uğurdan sonra AVTO çek çapı.
    // Prosess bitənə qədər popup bağlana bilməz; yalnız imtina/xəta olanda bağlanır.
    const handleOrderPos = async () => {
        if (!currentOrderId || !order?.items?.length) {
            toast.error('Əvvəlcə məhsul əlavə edin.');
            return;
        }
        if (!canFiscalRelay()) {
            toast.error('POS ödənişi yalnız Fiscal POS proqramında mümkündür.');
            return;
        }
        const amountLabel = `${Number(orderGrandTotal || 0).toFixed(2)} ₼`;
        setPosBusy(true);

        // Bir cəhd: prepare → cihaza göndər (kart gözlə) → bağla. Mesajları mərhələ-mərhələ yenilə.
        const posMsg = (message) => setPosModal({ open: true, closable: false, error: false, done: false, message });
        const attempt = async (forced = false) => {
            const extra = forced ? { force_sayim: true } : {};
            posMsg(`${amountLabel} hesab POS terminala göndərilir...`);
            let prep = await api.post(`/orders/${currentOrderId}/pay-prepare`, { method: 'kart', ...extra });

            // Caspos + TapXPhone: əvvəl kartı terminalda çək (aç→yoxla→rrn), sonra fiskal satış
            let tapx_rrn = '';
            if (prep.data?.tapx_required) {
                const res = await tapxCardCharge(currentOrderId, prep.data.tapx_amount || orderGrandTotal, posMsg);
                tapx_rrn = res.rrn;
                posMsg('Kart qəbul edildi. Fiskal satış göndərilir...');
                prep = await api.post(`/orders/${currentOrderId}/pay-prepare`, { method: 'kart', tapx_rrn, ...extra });
            }

            if (!prep.data?.relay) throw new Error('POS ödənişi üçün fiskal kassa aktiv deyil.');
            if (!tapx_rrn) posMsg(`${amountLabel} POS-a göndərildi. Müştəri kartı yaxınlaşdırsın — cavab gözlənilir...`);
            const fwd = await fiscalForward(prep.data.relay); // SmartOne/integrasiya: kart burda çəkilir
            posMsg('Ödəniş qəbul edildi. Hesab bağlanır...');
            await api.post(`/orders/${currentOrderId}/pay`, { method: 'kart', fiscal_raw: fwd.raw, fiscal_status: fwd.status, tapx_rrn, ...extra });
            return true;
        };

        try {
            let ok;
            try {
                ok = await attempt(false);
            } catch (err) {
                // Sayım açıqdır (admin) → popup-u gizlət, təsdiq soruş, "bəli"də force ilə təkrar
                if (err.response?.data?.code === 'SAYIM_CONFIRM') {
                    setPosModal({ open: false, message: '', closable: false, error: false, done: false });
                    if (await confirm(err.response?.data?.msg || 'Sayım bağlanmayıb. Yenə də təsdiq edək?')) {
                        ok = await attempt(true);
                    } else { setPosBusy(false); return; }
                } else { throw err; }
            }
            if (!ok) { setPosBusy(false); return; }
            // Uğur → müştəri çekini AVTO çap et (Hesab ekranı açılmır), sonra Zala keç
            setPosModal({ open: true, closable: false, error: false, done: false, message: 'Müştəri çeki çap olunur...' });
            try { await printCustomerReceiptCore(false); } catch { /* çap alınmasa da ödəniş bağlanıb */ }
            setPosModal({ open: true, closable: true, error: false, done: true, message: `✓ ${amountLabel} ödəniş uğurlu — hesab bağlandı.` });
            setTimeout(() => {
                setPosModal({ open: false, message: '', closable: false, error: false, done: false });
                navigate('/waiter/tables');
            }, 1600);
        } catch (err) {
            // İmtina / kartda pul yox / xəta → popup bağlana bilər, hesab AÇIQ qalır
            setPosModal({
                open: true, closable: true, error: true, done: false,
                message: err.response?.data?.msg || err.message || 'Ödəniş imtina edildi / baş tutmadı. Hesab açıq qalır.'
            });
        } finally {
            setPosBusy(false);
        }
    };

    // Müştəri çekini (hədiyyə biletləri + çek) çap et — həm birbaşa düymə, həm POS-dan
    // sonra AVTO çap üçün ortaq nüvə. markPrinted=false → yalnız çap (POS bağlanandan sonra).
    const printCustomerReceiptCore = async (markPrinted = true) => {
        const receiptPrinter = await getReceiptPrinterName();
        // Hədiyyə biletləri (varsa) — atomik claim/commit
        try {
            const pend = await api.post(`/orders/${currentOrderId}/gift-tickets/pending`);
            const pending = Number(pend.data?.pending || 0);
            for (let i = 0; i < pending; i++) {
                if (i > 0) await new Promise(r => setTimeout(r, 800));
                let ok = false;
                for (let attempt = 0; attempt < 3 && !ok; attempt++) {
                    try {
                        await printGift(receiptPrinter, { receiptLogoUrl: getReceiptLogoUrl(user), orderId: order.id, grandTotal: orderGrandTotal });
                        ok = true;
                    } catch (oneErr) { await new Promise(r => setTimeout(r, 500)); }
                }
                if (!ok) break;
                try { const c = await api.post(`/orders/${currentOrderId}/gift-tickets/commit`); if (!c.data?.committed) break; } catch { break; }
            }
        } catch { /* hədiyyə bileti kritik deyil */ }

        await printBill(receiptPrinter, {
            title: getReceiptTitle(user),
            receiptLogoUrl: getReceiptLogoUrl(user),
            phone: order.tenant_phone || '',
            orderId: order.id,
            dailyNumber: order.daily_number || null,   // fast food gündəlik sıra nömrəsi
            tableName: order.table_name,
            waiterName: order.waiter_name,
            subtotal: orderSubtotal,
            servicePercent: orderServicePercent,
            serviceAmount: orderServiceAmount,
            discountAmount: orderDiscountAmount,
            depositAmount: orderDepositAmount,
            appliedDeposit: orderAppliedDeposit,
            grandTotal: orderGrandTotal,
            total: orderGrandTotal,
            note: order.note || null,
            receiptText: buildReceiptText(order, user, isTakeaway),
            items: groupSetItems(order.items || []).map((item) => ({
                name: item._isSetGroup ? `[SET] ${item.item_name}` : item.item_name,
                qty: Number(item.qty),
                qtyLabel: item._isSetGroup ? `${item.qty}ed` : formatQty(item.qty, item.sale_type),
                unitPrice: Number(item.unit_price),
                saleType: item._isSetGroup ? 'piece' : (item.sale_type || 'piece'),
                note: item._isSetGroup ? null : (item.note || null),
                total: parseFloat(item.unit_price) * Number(item.qty)
            }))
        });

        if (markPrinted) {
            await api.post(`/orders/${currentOrderId}/mark-bill-printed`);
            await loadOrder(currentOrderId);
        }
    };

    const printCustomerBillDirect = async () => {
        if (!currentOrderId || !order?.items?.length) {
            toast.error('Əvvəlcə məhsul əlavə edin.');
            return;
        }

        if (isFreePlan(user?.tenant_plan)) {
            setShowUpgradeModal(true);
            return;
        }

        setPrinting(true);

        try {
            await printCustomerReceiptCore(true);
            toast.success('Müştəri çeki printerə göndərildi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Çap baş tutmadı.');
        } finally {
            setPrinting(false);
        }
    };

    const printGroup = async (currentOrder, group) => {
        const config = await window.qz.configs.create(group.printer);
        const time = fmtTime(new Date());

        const lines = [
            '\x1B\x40',
            '\x1B\x61\x01',
            '\x1B\x21\x30',
            `${currentOrder.table_name}\n`,
            '\x1B\x21\x00',
            `Sifariş #${currentOrder.id} · ${time}\n`,
            '--------------------------------\n',
            '\x1B\x61\x00'
        ];

        group.items.forEach((item) => {
            lines.push(`${item.qty_label || formatQty(item.qty, item.sale_type)} ${item.item_name}\n`);
            if (item.note) {
                lines.push(`   >> ${item.note}\n`);
            }
        });

        lines.push('--------------------------------\n\n\n');
        lines.push('\x1D\x56\x41\x10');
        await window.qz.print(config, lines);
    };

    // Admin/kassir reprint: backend-də unlock edir → bütün məhsulları yenidən göndərir
    const sendKitchenForce = async () => {
        setPrinting(true);
        try {
            // Köhnə "göndərilib" işarəsini sil
            const unlockRes = await api.post(`/printers/order/${currentOrderId}/unlock`, {});
            if (!unlockRes.data?.ok) {
                throw new Error(unlockRes.data?.msg || 'Unlock alınmadı.');
            }

            // Atomik claim — unlock-dan sonra delta-nı tutub dərhal göndərilmiş işarələ
            const res = await api.post(`/printers/order/${currentOrderId}/claim-kitchen`);
            const groups = Array.isArray(res.data?.groups) ? res.data.groups.filter(g => g && g.printer) : [];
            if (groups.length === 0) {
                toast.error('Göndəriləcək məhsul yoxdur.');
                return;
            }
            try {
                for (const group of groups) {
                    if (!group?.printer) continue;
                    try {
                        await printKitchen(group.printer, buildKitchenTicketText(res.data.order, group, kitchenQtyX), group.ip);
                    } catch (printErr) {
                        if (window.qz && window.qz.websocket.isActive()) await printGroup(res.data.order, group);
                        else throw printErr;
                    }
                }
            } catch (printErr) {
                // Çap alınmadı → claim-i geri aç (yenidən cəhd üçün)
                await api.post(`/printers/order/${currentOrderId}/unlock`, {}).catch(() => {});
                throw printErr;
            }
            toast.success('Mətbəxə yenidən göndərildi.');
            await loadOrder(currentOrderId);
        } catch (err) {
            console.error('[sendKitchenForce]', err);
            toast.error(err.response?.data?.msg || err.message || 'Yenidən göndərilmədi.');
        } finally {
            setPrinting(false);
        }
    };

    const sendToKitchen = async (forceReprint = false) => {
        if (!currentOrderId) {
            toast.error('Əvvəlcə məhsul əlavə edin.');
            return;
        }

        if (isFreePlan(user?.tenant_plan)) {
            setShowUpgradeModal(true);
            return;
        }

        // Admin/kassir üçün təkrar çap təsdiqi (custom modal, browser confirm yox)
        const isAdminUser = ['admin', 'superadmin', 'kassir'].includes(user?.role) || !!user?.is_manager;
        if (!hasUnsentItems && isAdminUser && !forceReprint) {
            const printedNames = (order?.items || [])
                .filter(it => Number(it.kitchen_printed || 0) === 1 && !it.custom_name)
                .map(it => it.item_name)
                .filter(Boolean)
                .slice(0, 8)
                .join(', ');
            const extra = (order?.items || []).filter(it => Number(it.kitchen_printed || 0) === 1).length > 8 ? ' …' : '';
            setReprintConfirm({ open: true, names: (printedNames || '—') + extra });
            return;
        }

        setPrinting(true);

        try {
            // ATOMIK claim — delta tutulub dərhal göndərilmiş işarələnir (heç vaxt 2 dəfə çıxmaz)
            const res = await api.post(`/printers/order/${currentOrderId}/claim-kitchen`);
            const groups = res.data.groups;

            if (!groups || groups.length === 0) {
                toast.error('Göndəriləcək yeni məhsul yoxdur.');
                return;
            }

            // Hər qrupu ayrıca çap et; uğursuzları qeyd et (AMMA claim-i geri AÇMA).
            // Claim atomikdir → göndərilmiş məhsul artıq işarələnib. Avtomatik geri açsaq,
            // agent kağızı çap edib təsdiq qaytarmasa belə (ACK gecikməsi) məhsul yenidən
            // "göndərilməmiş" olur və TƏKRAR çap olunur. İstifadəçi tələbi: 1 dəfə göndərilibsə
            // admin "yenidən göndər" edənə qədər birdə çap olmasın.
            const failedPrinters = [];
            for (const group of groups) {
                try {
                    await printKitchen(group.printer, buildKitchenTicketText(res.data.order, group, kitchenQtyX), group.ip);
                } catch (printErr) {
                    if (window.qz && window.qz.websocket.isActive()) {
                        try { await printGroup(res.data.order, group); }
                        catch { failedPrinters.push(group.printer_label || group.printer); }
                    } else {
                        failedPrinters.push(group.printer_label || group.printer);
                    }
                }
            }

            if (failedPrinters.length) {
                toast.error(`⚠️ Təsdiq gəlmədi: ${[...new Set(failedPrinters)].join(', ')}. Kağız çıxmayıbsa, admin "yenidən göndər" ilə təkrar göndərsin.`);
            } else {
                toast.success('Mətbəxə göndərildi.');
            }
            await loadOrder(currentOrderId);
        } catch (err) {
            console.error('[sendToKitchen]', err);
            toast.error(err.response?.data?.msg || err.message || 'Mətbəxə göndərilmədi.');
        } finally {
            setPrinting(false);
        }
    };

    // #7 — Zala qayıdarkən: ofisiant "mətbəxə göndər" vurmasa belə, çap olunmamış
    // (delta) məhsullar varsa səssiz avtomatik mətbəxə göndər. Artıq çap olunmuşlar
    // təkrar OLUNMUR (server yalnız qty > printed_qty olan hissəni qaytarır).
    const autoSendUnsentToKitchen = async () => {
        try {
            // ATOMIK claim — eyni anda başqa POS/əl ilə göndərmə olsa belə yalnız biri alır
            const res = await api.post(`/printers/order/${currentOrderId}/claim-kitchen`);
            const groups = res.data.groups;
            if (!groups || groups.length === 0) return false;

            // Claim atomikdir — çap uğursuz sayılsa belə geri AÇMIRIQ (təkrar çapın qarşısını alır).
            for (const group of groups) {
                try {
                    await printKitchen(group.printer, buildKitchenTicketText(res.data.order, group, kitchenQtyX), group.ip);
                } catch (printErr) {
                    if (window.qz && window.qz.websocket.isActive()) {
                        try { await printGroup(res.data.order, group); } catch { /* claim sabit qalır */ }
                    }
                }
            }
            return true;
        } catch (err) {
            console.error('[autoSendUnsentToKitchen]', err);
            return false;
        }
    };

    // Zala qayıdanda: mətbəxə getmiş məhsullardan silinən/azaldılanlar üçün "Ləğv edilmə"
    // çeki (üstündən xətli). Server gözləyən ləğvləri tutur (bir dəfə çıxır). Hər '−'-də
    // yox — yalnız Zala qayıdanda. Arxa planda işləyir (donma olmasın).
    const flushKitchenCancellations = async () => {
        try {
            const res = await api.post(`/printers/order/${currentOrderId}/claim-cancellations`);
            const groups = res.data.groups;
            if (!groups || groups.length === 0) return false;
            for (const group of groups) {
                if (!group?.printer) continue;
                try {
                    await printKitchen(group.printer, buildKitchenCancelTicketText(res.data.order, group, kitchenQtyX), group.ip);
                } catch { /* claim sabit qalır — təkrar çıxmasın */ }
            }
            return true;
        } catch (err) {
            console.error('[flushKitchenCancellations]', err);
            return false;
        }
    };

    // Zala qayıt — çap olunmamış məhsulları mətbəxə göndər (arxa planda) + Zala keç.
    // sendUnsent=false olsa yalnız ləğv çeki göndərilir, yeni məhsullar GÖNDƏRİLMİR.
    const doBackToTables = async (sendUnsent = true) => {
        // OFFLINE: claim/ləğv API-ləri işləmir → göndərilməmişləri lokal çap et (C# / LAN), sonra Zala
        if (order?._offline || offExistingMode) {
            if (sendUnsent && offlineUnsentCount() > 0 && !isFreePlan(user?.tenant_plan)) {
                try { await offlineSendKitchen(); } catch { /* ignore */ }
            }
            navigate('/waiter/tables');
            return;
        }
        if (sendUnsent && currentOrderId && hasUnsentItems && !isFreePlan(user?.tenant_plan)) {
            autoSendUnsentToKitchen()
                .then((ok) => { if (ok) toast.success('Çap olunmayan məhsullar mətbəxə göndərildi.'); })
                .catch(() => {});
        }
        // Ləğv çeki — mətbəxə getmiş məhsul silinib/azaldılıbsa (arxa planda) — həmişə
        if (currentOrderId && !isFreePlan(user?.tenant_plan)) {
            flushKitchenCancellations()
                .then((ok) => { if (ok) toast('🗑️ Ləğv çeki mətbəxə göndərildi.'); })
                .catch(() => {});
        }
        navigate('/waiter/tables');
    };

    const handleBackToTables = () => {
        // Superadmin tick + çap olunmamış məhsul varsa → əvvəlcə SORUŞ (popup).
        if (confirmKitchenExit && currentOrderId && hasUnsentItems && !isFreePlan(user?.tenant_plan)) {
            setExitConfirmModal(true);
            return;
        }
        doBackToTables(true);
    };

    const unlockKitchenPrinter = async (printer) => {
        try {
            await api.post(`/printers/order/${currentOrderId}/unlock`, { printer });
            toast.success('Çap kilidi açıldı.');
            setUnlockModal(false);
            await loadOrder(currentOrderId);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Kilid açıla bilmədi.');
        }
    };

    const orderSubtotal = (order?.items?.reduce(
        (sum, orderItem) => sum + parseFloat(orderItem.unit_price) * Number(orderItem.qty),
        0
    ) || 0) + (offAdds || []).reduce((s, a) => s + Number(a.unit_price || 0) * Number(a.qty || 0), 0);
    // Offline-da mövcud açıq sifariş rejimi (render üçün — netOffline reaktivdir)
    const offExistingMode = offlineEnabled() && graceValidToday() && netOffline && !!currentOrderId && !order?._offline;
    const isTakeaway = Number(order?.is_takeaway || 0) === 1;
    const orderServicePercent = toMoney(order?.service_percent);
    const orderServiceAmount = toMoney(order?.service_amount);
    const orderDiscountAmount = toMoney(order?.discount_amount);
    const orderDepositAmount = toMoney(order?.deposit_amount);
    const orderAppliedDeposit = toMoney(order?.applied_deposit);
    const orderGrandTotal = toMoney(order?.grand_total || orderSubtotal + orderServiceAmount - orderDiscountAmount + orderAppliedDeposit);
    const hasKitchenPrintedItems = (order?.items || []).some((orderItem) => Number(orderItem?.printed_qty || 0) > 0);
    // Mətbəxə göndərilməmiş miqdar varsa true — qty > printed_qty (qismən də olsa)
    // Offline-da (yeni və ya online açılmış sifariş) → lokal hesab (offline göndərilənlər çıxılır)
    const hasUnsentItems = (order?._offline || offExistingMode)
        ? offlineUnsentCount() > 0
        : (order?.items || []).some((orderItem) =>
            Number(orderItem?.qty || 0) > Number(orderItem?.printed_qty || 0)
        );
    // PIN-logged admin/kassir has role='waiter' + is_manager=true — treat as admin
    // Password-logged kassir of course also treated as admin (manager)
    // Sadə kassir (simple_kassir) — manager kimi davranmasın
    const isAdmin = (['admin', 'superadmin', 'kassir'].includes(user?.role) || !!user?.is_manager) && !isSimpleKassir;
    // Admins can always cancel any order regardless of kitchen/bill print status
    const canCancelOrder = isAdmin;

    // Sətirin bütün miqdarı mətbəxə gedibsə (qismən deyil) tam kilid
    const isFullyPrinted = (orderItem) => Number(orderItem?.printed_qty || 0) >= Number(orderItem?.qty || 0) && Number(orderItem?.qty || 0) > 0;
    // note redaktəsi: çek çıxıbsa və ya tam göndərilibsə kilidli (admin istisna)
    const isItemLocked = (orderItem) => !canOverridePrintLock
        && (Number(order?.bill_printed || 0) === 1 || isFullyPrinted(orderItem));
    // − düyməsi: printed_qty-dən aşağı sala bilməz (ofisiant). + həmişə açıqdır.
    const canOnlyAppendToItem = (orderItem) => !canOverridePrintLock && isFullyPrinted(orderItem);

    // Azərbaycan hərfləri (İ/ı/Ə...) üçün locale-aware kiçik hərf — adi toLowerCase
    // 'İ'-ni düzgün çevirmir və axtarış (o cümlədən setlər) tapmırdı.
    const norm = (s) => String(s || '').toLocaleLowerCase('az');

    const getFilteredItems = (catId) => {
        const cat = menu.find((entry) => entry.id === catId);
        if (!cat) return [];
        if (!search) return cat.items;
        const q = norm(search);
        return cat.items.filter((item) => norm(item.name).includes(q));
    };

    // Bütün kateqoriyalardan axtarış — Setlər kateqoriyası da daxil (menu-da var).
    // ⚠️ Virtual kateqoriyalar (✨ Yeniliklər / ⭐ Favorilər) real məhsulların EYNİ id-li
    // kopyalarını saxlayır. Ona görə axtarışda bir məhsul bir neçə dəfə çıxa bilər (təkrar)
    // və eyni React key-i reconciliation-ı sındırıb köhnə kartların ekranda ilişməsinə səbəb
    // olur. id-yə görə təkrarsızlaşdırırıq → hər məhsul bir dəfə, key-lər unikal.
    const allSearchItems = search
        ? Array.from(
            menu
                .flatMap((cat) => (cat.items || []).filter((item) => norm(item.name).includes(norm(search))))
                .reduce((map, item) => (map.has(item.id) ? map : map.set(item.id, item)), new Map())
                .values()
        )
            // Son 3 ayda ən çox satılan öndə (backend `sold_90` göndərir)
            .sort((a, b) => Number(b.sold_90 || 0) - Number(a.sold_90 || 0))
        : null;

    const activeCategory = menu.find((entry) => entry.id === activeCat) || null;
    const tableTitle = order?.table_name || 'Masa';

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-white text-gray-400">
                Yüklənir...
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-[#f6f7f9] overflow-hidden">
            <nav className="bg-white border-b border-gray-200 min-h-11 2xl:h-14 flex items-center px-2 sm:px-3 2xl:px-4 gap-1.5 sm:gap-2 2xl:gap-3 flex-shrink-0 z-50">
                <button
                    onClick={handleBackToTables}
                    className="px-2 sm:px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-500 hover:border-primary-400 hover:text-primary-500 transition-all flex-shrink-0"
                >
                    ← <span className="hidden sm:inline">Zal</span>
                </button>
                <span className="font-bold text-gray-800 text-sm truncate min-w-0">{tableTitle}</span>
                {isTakeaway && (
                    <span className="text-[10px] sm:text-xs bg-orange-50 text-orange-600 border border-orange-200 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-semibold flex-shrink-0">
                        🥡<span className="hidden sm:inline"> Takeaway +5%</span>
                    </span>
                )}
                {currentOrderId && (
                    <span className="text-[10px] sm:text-xs bg-primary-50 text-primary-600 border border-primary-200 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-semibold flex-shrink-0">
                        #{currentOrderId}
                    </span>
                )}
                <div className="ml-auto flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    {canUnlockKitchen && currentOrderId && (
                        <button
                            onClick={() => setUnlockModal(true)}
                            className="px-2 sm:px-3 py-1.5 rounded-lg text-xs font-semibold border border-amber-200 text-amber-700 hover:bg-amber-50 transition-all"
                            title="Çap kilidini aç"
                        >
                            🔓<span className="hidden sm:inline ml-1">Çap kilidini aç</span>
                        </button>
                    )}
                    <button
                        onClick={async () => {
                            setSyncing(true);
                            await loadMenu();
                            if (currentOrderId) await loadOrder(currentOrderId);
                            setSyncing(false);
                            toast.success('Sinxronlaşdırıldı');
                        }}
                        disabled={syncing}
                        className="px-2 sm:px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-500 hover:border-primary-400 hover:text-primary-500 transition-all disabled:opacity-50"
                        title="Menyu və sifarişi yenilə"
                    >
                        {syncing ? '⏳' : '🔄'}
                    </button>
                    <span className="hidden sm:inline text-xs text-gray-400">
                        {order?.waiter_name && `👤 ${order.waiter_name}`}
                    </span>
                </div>
            </nav>

            <div className="flex flex-1 overflow-hidden">
                <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                    <div className="px-3 py-2 bg-white border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            {!search && activeCategory && (
                                <button
                                    onClick={() => setActiveCat(null)}
                                    className="px-3 py-1.5 rounded-xl text-sm font-bold border border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 transition-all whitespace-nowrap shadow-sm"
                                >
                                    ← Kateqoriyalara qayıt
                                </button>
                            )}
                            <div className="relative flex-1">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        if (e.target.value) {
                                            setActiveCat(null);
                                        }
                                    }}
                                    placeholder="🔍 Məhsul axtar..."
                                    className="w-full px-3 py-2 pr-9 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-400"
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs"
                                        aria-label="Təmizlə"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {!search && activeCategory && (
                        <div className="px-4 py-3 bg-white border-b border-gray-100 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-1">
                                    Kateqoriya
                                </div>
                                <div className="font-bold text-gray-800 truncate">
                                    {activeCategory.name}
                                </div>
                                <div className="text-xs text-gray-400">
                                    {activeCategory.items.length} məhsul
                                </div>
                            </div>
                            <div className="text-xs text-primary-500 font-semibold">
                                Məhsul seçin
                            </div>
                        </div>
                    )}

                    <div className="flex-1 min-h-0 overflow-y-auto p-2.5">
                        {search ? (
                            allSearchItems?.length === 0 ? (
                                <div key="view-search-empty" className="text-center text-gray-400 py-12">Məhsul tapılmadı.</div>
                            ) : (
                                <div key="view-search" className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                                    {allSearchItems.map((item) => (
                                        <MenuItemCard key={item.id} item={item} onAdd={addItem} />
                                    ))}
                                </div>
                            )
                        ) : activeCategory ? (
                            getFilteredItems(activeCat).length === 0 ? (
                                <div key="view-cat-empty" className="text-center text-gray-400 py-12">Bu kateqoriyada məhsul yoxdur.</div>
                            ) : (
                                <div key="view-cat" className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                                    {getFilteredItems(activeCat).map((item) => (
                                        <MenuItemCard key={item.id} item={item} onAdd={addItem} />
                                    ))}
                                </div>
                            )
                        ) : (
                            <div key="view-home">
                                <div className="flex items-end justify-between mb-4">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-1">
                                            Menyu
                                        </div>
                                        <h2 className="text-lg font-bold text-gray-800">Kateqoriya seçin</h2>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {menu.length} kateqoriya
                                    </div>
                                </div>

                                <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                                    {menu.map((category, index) => (
                                        <CategoryCard
                                            key={category.id}
                                            category={category}
                                            accent={getCategoryAccent(index)}
                                            onOpen={() => setActiveCat(category.id)}
                                        />
                                    ))}
                                    {/* Banket menyu — yalnız superadmin açıbsa (default deaktiv), ən sonda */}
                                    {isAdmin && banquetEnabled && (
                                        <button
                                            key="banquet-card"
                                            onClick={openBanquetModal}
                                            className="text-left rounded-3xl border-2 border-red-400 bg-gradient-to-br from-red-50 to-rose-100 p-2.5 min-h-[85px] 2xl:min-h-[108px] hover:-translate-y-0.5 hover:shadow-md transition-all"
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="w-10 h-10 rounded-2xl bg-white/80 border border-red-200 flex items-center justify-center text-lg shadow-sm">
                                                    🍽️
                                                </div>
                                                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
                                                    Banket
                                                </span>
                                            </div>
                                            <div className="font-bold text-sm text-gray-800 leading-snug mb-1">
                                                Banket menyu
                                            </div>
                                            <div className="text-[11px] text-gray-500 leading-snug line-clamp-2">
                                                Banket seçin və masaya əlavə edin.
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {!cartOpen && (
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        className="md:hidden fixed bottom-5 right-5 z-30 flex items-center gap-2 rounded-2xl bg-primary-600 px-4 py-3 text-sm font-bold text-white shadow-lg active:scale-95 transition-all"
                    >
                        <span className="text-base">🧾</span>
                        <span>Sifariş</span>
                        {(order?.items?.length || 0) > 0 && (
                            <span className="flex h-5 min-w-[20px] px-1 items-center justify-center rounded-full bg-white text-[11px] font-black text-primary-700">
                                {order.items.length}
                            </span>
                        )}
                    </button>
                )}

                <div className={`${cartOpen ? 'fixed inset-0 z-[60] flex' : 'hidden'} md:static md:flex md:z-auto md:w-[300px] 2xl:w-[420px] bg-white md:border-l border-gray-200 flex-col flex-shrink-0`}>
                    <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <div className="font-bold text-gray-800">🧾 Sifariş</div>
                            <div className="text-xs text-gray-400 mt-0.5 truncate">{tableTitle}</div>
                            {order?.opened_at && (
                                <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                                    🕐 {fmtDateTimeNum(order.opened_at)}-də açılıb
                                </div>
                            )}
                            {order?.note && (
                                <div className="mt-1 inline-flex items-center gap-1 rounded-lg bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-bold text-amber-800 max-w-full truncate">
                                    📌 {order.note}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {isAdmin && (order?.items?.length || 0) > 0 && (
                                <button
                                    type="button"
                                    onClick={() => { setShowAllTimes(v => !v); setTimeItemId(null); }}
                                    className={`rounded-lg border px-2 py-1 text-[11px] font-bold transition-all ${showAllTimes ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                                    title="Bütün məhsulların vaxtını göstər/gizlət"
                                >
                                    🕐 Saatlar
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setCartOpen(false)}
                                className="md:hidden w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
                                aria-label="Bağla"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto">
                        {!order?.items?.length ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 p-6">
                                <span className="text-3xl">🍽️</span>
                                <p className="text-sm text-center">
                                    Hələ məhsul əlavə edilməyib.
                                    <br />
                                    Soldan kateqoriya seçin.
                                </p>
                            </div>
                        ) : (
                            <div className="p-1.5">
                                {displayOrderItems.map((orderItem) => (
                                    <div
                                        key={orderItem.id}
                                        className="px-2 py-1.5 rounded-xl hover:bg-gray-50 transition-all"
                                    >
                                        {/* Row 1: name + price + delete */}
                                        <div className="flex items-center gap-1 mb-0.5">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-semibold text-gray-800 truncate leading-tight">
                                                    {orderItem._pendingOnly && <span className="mr-1 rounded bg-amber-100 px-1 py-0.5 text-[8px] font-bold text-amber-700 align-middle">📴 YENİ</span>}
                                                    {orderItem._pendingDelta > 0 && <span className="mr-1 rounded bg-amber-100 px-1 py-0.5 text-[8px] font-bold text-amber-700 align-middle">📴 +{orderItem._pendingDelta}</span>}
                                                    {orderItem._isSetGroup ? `🎁 ${orderItem.item_name}` : orderItem.item_name}
                                                </div>
                                                {Number(orderItem.is_hourly) === 1 && (
                                                    <div className="text-[10px] font-semibold text-indigo-600 leading-tight">
                                                        ⏱️ {fmtElapsed(orderItem.hourly_elapsed_sec)} · {Number(orderItem.hourly_rate || 0).toFixed(0)}₼/saat
                                                    </div>
                                                )}
                                            </div>
                                            <div className="font-bold font-mono text-primary-600 text-xs flex-shrink-0">
                                                {(parseFloat(orderItem.unit_price) * Number(orderItem.qty)).toFixed(2)} ₼
                                            </div>
                                            {!hideDelete && (
                                            <button
                                                disabled={orderItem._isSetGroup
                                                    ? (!canOverridePrintLock && (Number(orderItem.kitchen_printed || 0) === 1 || Number(order?.bill_printed || 0) === 1))
                                                    : (!canOverridePrintLock && (Number(order?.bill_printed || 0) === 1 || Number(orderItem.printed_qty || 0) > 0))}
                                                onClick={() => orderItem._isSetGroup ? deleteSetGroup(orderItem.set_group_id) : removeItem(orderItem.id)}
                                                title={!canOverridePrintLock && ((orderItem._isSetGroup && Number(orderItem.kitchen_printed || 0) === 1) || (!orderItem._isSetGroup && Number(orderItem.printed_qty || 0) > 0)) ? 'Mətbəxə gedib — yalnız admin silə bilər' : undefined}
                                                className="w-5 h-5 rounded-md text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all flex items-center justify-center text-sm flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-300 disabled:hover:bg-transparent"
                                            >
                                                ×
                                            </button>
                                            )}
                                        </div>
                                        {/* Row 2: badge + note + qty controls */}
                                        <div className="flex items-center gap-1">
                                            <div className="flex-1 flex items-center gap-1 min-w-0">
                                                {(() => {
                                                    const printed = Number(orderItem.printed_qty || 0);
                                                    const q = Number(orderItem.qty || 0);
                                                    if (orderItem._isSetGroup) {
                                                        // set qrupu üçün köhnə davranış (kitchen_printed)
                                                        return Number(orderItem.kitchen_printed || 0) === 1
                                                            ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 flex-shrink-0">Mətbəxə gedib</span>
                                                            : <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 flex-shrink-0">Yeni</span>;
                                                    }
                                                    if (printed >= q && q > 0) {
                                                        return <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 flex-shrink-0">Mətbəxə gedib</span>;
                                                    }
                                                    if (printed > 0) {
                                                        return <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold text-blue-700 flex-shrink-0">{printed} gedib · {q - printed} yeni</span>;
                                                    }
                                                    return <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 flex-shrink-0">Yeni</span>;
                                                })()}
                                                {!orderItem._isSetGroup && (orderItem.note ? (
                                                    <div
                                                        className={`text-[10px] truncate ${isItemLocked(orderItem) ? 'text-gray-400 cursor-not-allowed' : 'text-amber-500 cursor-pointer hover:text-amber-600'}`}
                                                        onClick={() => !isItemLocked(orderItem) && openNoteModal(orderItem)}
                                                    >
                                                        📝 {orderItem.note}
                                                    </div>
                                                ) : (
                                                    <button
                                                        disabled={isItemLocked(orderItem)}
                                                        onClick={() => openNoteModal(orderItem)}
                                                        className="text-[10px] text-gray-300 hover:text-primary-400 transition-all disabled:text-gray-300 disabled:cursor-not-allowed"
                                                    >
                                                        + qeyd
                                                    </button>
                                                ))}
                                                {isAdmin && !orderItem._isSetGroup && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setTimeItemId(timeItemId === orderItem.id ? null : orderItem.id)}
                                                        className={`text-[10px] transition-all flex-shrink-0 ${timeItemId === orderItem.id ? 'text-primary-600' : 'text-gray-300 hover:text-primary-400'}`}
                                                        title="Vaxt detalı (kassir/admin)"
                                                    >
                                                        🕐
                                                    </button>
                                                )}
                                            </div>
                                            {/* Qty controls */}
                                            {Number(orderItem.is_hourly) === 1 ? (
                                                <span className="min-w-[32px] text-center text-[10px] font-bold text-indigo-500 flex-shrink-0">
                                                    saatlıq
                                                </span>
                                            ) : orderItem._isSetGroup ? (
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    {!hideDelete && (
                                                    <button
                                                        disabled={!canOverridePrintLock && (Number(orderItem.kitchen_printed || 0) === 1 || Number(order?.bill_printed || 0) === 1)}
                                                        onClick={() => decrementSetGroup(orderItem.set_group_id)}
                                                        className="w-9 h-9 rounded-lg border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-all flex items-center justify-center font-bold text-lg flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        −
                                                    </button>
                                                    )}
                                                    <span className="min-w-[32px] text-center text-sm font-bold font-mono flex-shrink-0">
                                                        {orderItem.qty}ed
                                                    </span>
                                                    <button
                                                        disabled={viewOnly || !orderItem.set_id}
                                                        onClick={() => addSetGroup(orderItem.set_id)}
                                                        className="w-9 h-9 rounded-lg border border-gray-200 text-gray-500 hover:border-primary-400 hover:text-primary-500 hover:bg-primary-50 transition-all flex items-center justify-center font-bold text-lg flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            ) : (orderItem.sale_type || 'piece') === 'piece' ? (
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    {!hideDelete && (
                                                    <button
                                                        disabled={!canOverridePrintLock && (Number(order?.bill_printed || 0) === 1 || Number(orderItem.qty) <= Number(orderItem.printed_qty || 0))}
                                                        onClick={() => updateQty(orderItem.id, Number(orderItem.qty) - 1)}
                                                        title={!canOverridePrintLock && Number(order?.bill_printed || 0) === 1 ? 'Çek çıxıb — azaltmaq yalnız admin' : (!canOverridePrintLock && Number(orderItem.qty) <= Number(orderItem.printed_qty || 0) ? 'Mətbəxə gedən miqdar azaldıla bilməz' : undefined)}
                                                        className="w-9 h-9 rounded-lg border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-all flex items-center justify-center font-bold text-lg flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-500 disabled:hover:bg-transparent"
                                                    >
                                                        −
                                                    </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => openQtyModalForEdit(orderItem)}
                                                        title="Miqdarı yaz (keypad)"
                                                        className="min-w-[40px] text-center text-sm font-bold font-mono flex-shrink-0 rounded-lg border border-dashed border-gray-300 px-1.5 py-1.5 text-gray-800 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
                                                    >
                                                        {formatQty(orderItem.qty, orderItem.sale_type)}
                                                    </button>
                                                    <button
                                                        disabled={viewOnly}
                                                        onClick={() => updateQty(orderItem.id, Number(orderItem.qty) + 1)}
                                                        className="w-9 h-9 rounded-lg border border-gray-200 text-gray-500 hover:border-primary-400 hover:text-primary-500 hover:bg-primary-50 transition-all flex items-center justify-center font-bold text-lg flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-500 disabled:hover:bg-transparent"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    disabled={viewOnly || (!canOverridePrintLock && Number(order?.bill_printed || 0) === 1)}
                                                    onClick={() => openQtyModalForEdit(orderItem)}
                                                    className="px-2 py-1 rounded-lg border border-gray-200 text-xs font-bold text-gray-800 hover:border-gray-300 hover:bg-gray-50 transition-all flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-white"
                                                >
                                                    {formatQty(orderItem.qty, orderItem.sale_type)}
                                                </button>
                                            )}
                                        </div>
                                        {/* Row 3: vaxt detalı (yalnız kassir/admin — tək klik və ya "hamısı") */}
                                        {isAdmin && !orderItem._isSetGroup && (showAllTimes || timeItemId === orderItem.id) && (
                                            <div className="mt-1 rounded-lg bg-slate-50 border border-slate-200 px-2 py-1 text-[10px] text-slate-600 space-y-0.5">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-slate-400">🍽️ Vuruldu:</span>
                                                    <span className="font-semibold">{fmtTime(orderItem.created_at)} · {fmtAgo(orderItem.created_at)}</span>
                                                </div>
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-slate-400">🔥 Mətbəxə:</span>
                                                    {orderItem.kitchen_printed_at
                                                        ? <span className="font-semibold text-amber-700">{fmtTime(orderItem.kitchen_printed_at)} · {fmtAgo(orderItem.kitchen_printed_at)}</span>
                                                        : <span className="text-slate-400">hələ getməyib</span>}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-t border-gray-100 p-2 2xl:p-4 flex-shrink-0">
                        {viewOnly && (
                            <div className="mb-2 flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                                <span>👁️</span>
                                <span>Yalnız baxış rejimi. Məhsul əlavə etmək və dəyişmək bağlıdır, hesab bağlamaq olar.</span>
                            </div>
                        )}
                        {/* Mobil: aşağı paneli yığ/aç — daha çox məhsul görmək üçün */}
                        <button
                            type="button"
                            onClick={() => setCartPanelCollapsed(v => !v)}
                            className="md:hidden mb-1.5 flex w-full items-center justify-center gap-2 rounded-lg bg-gray-100 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200"
                        >
                            {cartPanelCollapsed
                                ? `▲ Aç — Cəmi ${orderGrandTotal.toFixed(2)} ₼`
                                : '▼ Paneli gizlət (daha çox məhsul gör)'}
                        </button>
                        <div className={cartPanelCollapsed ? 'hidden md:block' : ''}>
                        {currentOrderId && isAdmin && (
                            <div className="mb-1.5">
                                <button
                                    onClick={() => setShowNote(!showNote)}
                                    className="text-xs text-amber-600 font-semibold underline"
                                >
                                    📌 {showNote ? 'Masa qeydini bağla' : (order?.note ? 'Masa qeydini düzəlt' : 'Masaya qeyd əlavə et')}
                                </button>
                                {showNote && (
                                    <div className="mt-2">
                                        <textarea
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                            rows={2}
                                            placeholder="Bütün masa üçün qeyd (məs: For Boss)"
                                            className="w-full px-3 py-2 border border-amber-200 rounded-xl text-xs resize-none focus:outline-none focus:border-amber-400"
                                        />
                                        <p className="mt-1 text-[10px] text-gray-400">Bütün masaya aiddir, mətbəx sifarişi deyil. Yalnız kassir/admin yaza bilər.</p>
                                        <button
                                            onClick={saveNote}
                                            className="mt-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 transition-all"
                                        >
                                            Yadda saxla
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs font-semibold text-gray-500">Cəmi:</span>
                            <span className="text-xl 2xl:text-2xl font-bold font-mono text-gray-800">
                                {orderGrandTotal.toFixed(2)} ₼
                            </span>
                        </div>

                        <div className="mb-1.5 rounded-xl border border-gray-200 bg-gray-50 p-1.5 2xl:p-3">
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                Məbləğ detalları
                            </div>
                            <div className="space-y-0.5 border-t border-dashed border-gray-200 pt-1 text-[11px]">
                                <div className="flex items-center justify-between text-gray-500">
                                    <span>Cəm</span>
                                    <span className="font-mono">{formatMoney(orderSubtotal)}</span>
                                </div>
                                <div className="flex items-center justify-between text-gray-500">
                                    <span>
                                        {isTakeaway
                                            ? `Takeaway üçün əlavə ${trimQty(orderServicePercent, 2)}%`
                                            : `${trimQty(orderServicePercent, 2)}% servis haqqı`}
                                    </span>
                                    <span className="font-mono">{formatMoney(orderServiceAmount)}</span>
                                </div>
                                <div className="flex items-center justify-between text-gray-500">
                                    <span>Depozit</span>
                                    <span className="font-mono">{formatMoney(orderAppliedDeposit)}</span>
                                </div>
                                <div className="flex items-center justify-between text-gray-500">
                                    <span>Endirim</span>
                                    <span className="font-mono">{formatMoney(orderDiscountAmount)}</span>
                                </div>
                                {orderDepositAmount > 0 && (
                                    <div className="flex items-center justify-between text-[11px] font-semibold text-amber-700">
                                        <span>Depozit hədəfi</span>
                                        <span className="font-mono">{formatMoney(orderDepositAmount)}</span>
                                    </div>
                                )}
                                <div className="mt-1 flex items-center justify-between border-t border-dashed border-gray-200 pt-1 text-xs font-bold text-gray-800">
                                    <span>Ümumi cəm</span>
                                    <span className="font-mono">{formatMoney(orderGrandTotal)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Maya (cost) — yalnız admin/menecer */}
                        {isAdmin && (order?.items?.length || 0) > 0 && (
                            <div className="mb-2">
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={toggleMaya}
                                        className="flex-1 rounded-xl border border-dashed border-gray-300 py-2 text-xs font-bold text-gray-600 transition-all hover:bg-gray-50"
                                    >
                                        {mayaOpen ? '🧮 Mayanı gizlət' : '🧮 Masanın mayası'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setWaModal(true)}
                                        title="Sifarişi WhatsApp ilə göndər"
                                        className="flex-1 rounded-xl border border-green-200 bg-green-50 py-2 text-xs font-bold text-green-700 transition-all hover:bg-green-100"
                                    >
                                        📱 WhatsApp
                                    </button>
                                </div>
                                {mayaOpen && (
                                    <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs">
                                        {mayaLoading ? (
                                            <div className="py-2 text-center text-gray-400">Hesablanır...</div>
                                        ) : mayaData ? (
                                            <>
                                                <div className="space-y-1">
                                                    {mayaData.items.map((it, i) => (
                                                        <div key={i} className="flex justify-between text-gray-500">
                                                            <span className="truncate pr-2">{it.name} × {trimQty(it.qty, 3)}</span>
                                                            <span className="whitespace-nowrap font-mono">{formatMoney(it.line_cost)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 font-bold text-gray-800">
                                                    <span>Maya (cəmi)</span>
                                                    <span className="font-mono">{formatMoney(mayaData.total_cost)}</span>
                                                </div>
                                                <div className="mt-1 flex justify-between font-bold text-emerald-700">
                                                    <span>Mənfəət (satış − maya)</span>
                                                    <span className="font-mono">{formatMoney(orderGrandTotal - Number(mayaData.total_cost || 0))}</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="py-2 text-center text-gray-400">Məlumat yoxdur.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex flex-col gap-1">
                            {/* ── OFFLINE düymələr — online ilə EYNİ icazələr ──
                                 Mətbəxə göndər: hamı · Hesabı bağla: yalnız admin/kassir/menecer (sadə kassir yox)
                                 Müştəri çeki: hamı · Ofisiant hesab bağlamır (online-da da yalnız POS terminalı ilə) */}
                            {(order?._offline || offExistingMode) && (() => {
                                const listLen = order?._offline ? (order?.items?.length || 0) : ((order?.items?.length || 0) + (offAdds?.length || 0));
                                const unsent = offlineUnsentCount();
                                return (
                                <>
                                    {offExistingMode && (
                                        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] font-bold text-amber-700 leading-snug">
                                            📴 Offline. Yeni məhsul əlavə etmək olar, internet qayıdanda sinxronlaşacaq.
                                        </div>
                                    )}
                                    {showKitchenButton && (
                                        <button
                                            onClick={offlineSendKitchen}
                                            disabled={!unsent || printing}
                                            className="w-full py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: unsent ? 'linear-gradient(135deg, #d35400, #e67e22)' : 'linear-gradient(135deg, #9ca3af, #6b7280)' }}
                                        >
                                            {printing ? '⏳ Göndərilir...' : (unsent ? '🔥 Mətbəxə göndər' : '✓ Mətbəxə göndərilib')}
                                        </button>
                                    )}
                                    {canCloseTable ? (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={openOfflineClose}
                                                disabled={!listLen}
                                                className="flex-1 py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                                style={{ background: 'linear-gradient(135deg, #1a7a40, #27AE60)' }}
                                            >
                                                💳 Hesabı bağla
                                            </button>
                                            <button
                                                onClick={offlinePrintBill}
                                                disabled={!listLen || printing}
                                                className="flex-1 py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                                style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                            >
                                                🖨️ Müştəri
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={offlinePrintBill}
                                            disabled={!listLen || printing}
                                            className="w-full py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #1a7a40, #27AE60)' }}
                                        >
                                            {printing ? '⏳ Çap olunur...' : '🖨️ Müştəri (çap)'}
                                        </button>
                                    )}
                                    {order?._offline ? (
                                        <button
                                            onClick={confirmOfflineOrder}
                                            disabled={!listLen}
                                            className="w-full py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #b45309, #d97706)' }}
                                        >
                                            📴 Masanı saxla (açıq qalsın)
                                        </button>
                                    ) : (
                                        <button
                                            onClick={offlineSendAddItems}
                                            disabled={!offAdds.length}
                                            className="w-full py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #b45309, #d97706)' }}
                                        >
                                            📴 Əlavələri saxla{offAdds.length ? ` (${offAdds.length})` : ''}
                                        </button>
                                    )}
                                    {order?._offline ? (
                                        <button
                                            type="button"
                                            onClick={() => { clearOffDraft(tableId); navigate('/waiter/tables'); }}
                                            className="w-full py-1 2xl:py-2 rounded-xl font-semibold text-xs border border-gray-200 text-gray-400 hover:bg-gray-50"
                                        >
                                            ✕ Ləğv et (səbəti sil)
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => navigate('/waiter/tables')}
                                            className="w-full py-1 2xl:py-2 rounded-xl font-semibold text-xs border border-gray-200 text-gray-400 hover:bg-gray-50"
                                        >
                                            ← Zala qayıt
                                        </button>
                                    )}
                                </>
                                );
                            })()}
                            {!order?._offline && !offExistingMode && showKitchenButton && (() => {
                                // Waiter: yeni göndəriləcək məhsul yoxdursa düymə greyed
                                // Admin/kassir: həmişə aktiv, klik edəndə təsdiq istəyir
                                const isMgr = isAdmin; // admin/kassir/superadmin/manager
                                const noNew = !hasUnsentItems;
                                const disabled = !order?.items?.length || printing || (noNew && !isMgr);
                                return (
                                    <button
                                        onClick={() => sendToKitchen(false)}
                                        disabled={disabled}
                                        title={noNew && !isMgr ? 'Bütün məhsullar artıq mətbəxə göndərilib' : undefined}
                                        className="w-full py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                        style={{
                                            background: disabled
                                                ? 'linear-gradient(135deg, #9ca3af, #6b7280)'
                                                : (noNew && isMgr
                                                    ? 'linear-gradient(135deg, #6b7280, #9ca3af)'
                                                    : 'linear-gradient(135deg, #d35400, #e67e22)')
                                        }}
                                    >
                                        {printing
                                            ? '⏳ Göndərilir...'
                                            : (noNew
                                                ? (isMgr ? '🔄 Yenidən mətbəxə (təsdiq)' : '✓ Mətbəxə göndərilib')
                                                : '🔥 Mətbəxə göndər')}
                                    </button>
                                );
                            })()}

                            {!order?._offline && !offExistingMode && (canFiscalRelay() && posTerminal ? (
                                canCloseTable ? (
                                    // Menecer (admin/kassir) → iki düymə: sol Hesab (ekran, tam seçim), sağ POS-a göndər
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => navigate(`/waiter/bill/${currentOrderId}`)}
                                            disabled={!order?.items?.length || !currentOrderId || posBusy}
                                            className="flex-1 py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #1a7a40, #27AE60)' }}
                                        >
                                            🧾 Hesab
                                        </button>
                                        <button
                                            onClick={handleOrderPos}
                                            disabled={!order?.items?.length || !currentOrderId || posBusy}
                                            className="flex-1 py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                        >
                                            {posBusy ? '⏳ POS...' : '💳 POS-a göndər'}
                                        </button>
                                    </div>
                                ) : (
                                    // Ofisiant → 2 düymə: Müştəri (çeki çap et) + POS-a göndər (popup/proses)
                                    <div className="flex gap-2">
                                        <button
                                            onClick={printCustomerBillDirect}
                                            disabled={!order?.items?.length || !currentOrderId || printing || posBusy}
                                            className="flex-1 py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #1a7a40, #27AE60)' }}
                                        >
                                            {printing ? '⏳ Çap...' : '🖨️ Müştəri'}
                                        </button>
                                        <button
                                            onClick={handleOrderPos}
                                            disabled={!order?.items?.length || !currentOrderId || posBusy}
                                            className="flex-1 py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                            style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                        >
                                            {posBusy ? '⏳ POS...' : '💳 POS-a göndər'}
                                        </button>
                                    </div>
                                )
                            ) : (
                                <button
                                    onClick={() => {
                                        if (canCloseTable) { navigate(`/waiter/bill/${currentOrderId}`); return; }
                                        printCustomerBillDirect();
                                    }}
                                    disabled={!order?.items?.length || !currentOrderId || (!canCloseTable && printing)}
                                    className="w-full py-1.5 2xl:py-3 rounded-xl font-bold text-white text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
                                    style={{ background: 'linear-gradient(135deg, #1a7a40, #27AE60)' }}
                                >
                                    {canCloseTable ? '💳 Hesabı bağla' : (printing ? '⏳ Çap olunur...' : '🖨️ Müştəri (çap)')}
                                </button>
                            ))}

                            {/* Digər əməliyyatlar — yığcam menyu (masaya girəndə bağlı) */}
                            {!order?._offline && !offExistingMode && (
                            <>
                            <button
                                type="button"
                                onClick={() => setMoreActionsOpen(v => !v)}
                                className={`w-full py-1 2xl:py-2 rounded-xl font-semibold text-xs border transition-all flex items-center justify-center gap-1.5 ${moreActionsOpen ? 'text-gray-600 border-gray-300 bg-gray-50' : 'text-gray-400 border-gray-200 hover:bg-gray-50'}`}
                            >
                                {moreActionsOpen ? '▲ Gizlət' : '⋯ Digər əməliyyatlar'}
                            </button>

                            {moreActionsOpen && (
                            <>
                            {/* Çek tarixçəsi — HƏLƏLİK yalnız admin (kassir/administrator yox) */}
                            {(user?.role === 'admin' || user?.role === 'superadmin' || user?.display_role === 'admin') && (
                            <button
                                onClick={openPrintLog}
                                disabled={!currentOrderId}
                                className="w-full py-1 2xl:py-2.5 rounded-xl font-semibold text-teal-600 text-sm border border-teal-100 hover:bg-teal-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                🧾 Çek tarixçəsi (çap vaxtları)
                            </button>
                            )}
                            {/* Silmə/azaltma bağlıdırsa (o cümlədən "baş admin da bilməsin") bu əməliyyatlar da gizlənir */}
                            {!hideDelete && (canCloseTable || Number(user?.waiter_add_custom_item || 0) === 1) && (
                                <button
                                    onClick={openCustomModal}
                                    className="w-full py-1 2xl:py-2.5 rounded-xl font-semibold text-amber-700 text-sm border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    ➕ Əlavə menyu (manual)
                                </button>
                            )}
                            {!hideDelete && (
                            <button
                                onClick={openMoveModal}
                                disabled={!currentOrderId}
                                className="w-full py-1 2xl:py-2.5 rounded-xl font-semibold text-sky-600 text-sm border border-sky-100 hover:bg-sky-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                ↔ Köçür / birləşdir
                            </button>
                            )}

                            {!hideDelete && (['admin', 'superadmin', 'kassir'].includes(user?.role) || user?.is_manager) && (
                                <button
                                    onClick={openWaiterChange}
                                    disabled={!currentOrderId}
                                    className="w-full py-1 2xl:py-2.5 rounded-xl font-semibold text-violet-600 text-sm border border-violet-100 hover:bg-violet-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    👤 Ofisianti dəyiş
                                </button>
                            )}

                            {!(hideDelete && currentOrderId) && (
                            <button
                                onClick={() => {
                                    if (!currentOrderId) {
                                        navigate('/waiter/tables');
                                        return;
                                    }

                                    if (!isAdmin) {
                                        toast.error('Sifarişi ləğv etmək üçün admin icazəsi lazımdır.');
                                        return;
                                    }

                                    setCancelModal(true);
                                }}
                                className="w-full py-1 2xl:py-2.5 rounded-xl font-semibold text-red-400 text-sm border border-red-100 hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={currentOrderId ? !canCancelOrder : false}
                            >
                                🚫 {currentOrderId ? 'Sifarişi ləğv et' : 'Geri qayıt'}
                            </button>
                            )}
                            </>
                            )}
                            </>
                            )}
                        </div>
                        </div>
                    </div>
                </div>
            </div>

            {offPayModal.open && (order?._offline || offExistingMode) && (() => {
                const fin = offlineFinancials(offlineCloseItems(), offPayDiscount(), !order?._offline && Number(order?.service_exempt || 0) === 1);
                const cardPart = offPayModal.method === 'kart' || (offPayModal.method === 'hesab' && Number(String(offPayModal.card || '').replace(',', '.')) > 0);
                return (
                <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-4"
                     onClick={(e) => e.target === e.currentTarget && setOffPayModal({ ...offPayModal, open: false })}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg text-gray-800 mb-1">📴 Offline hesabı bağla</h3>
                        <p className="text-xs text-gray-400 mb-3">Fiskal və kassa internet qayıdanda tamamlanacaq.</p>

                        <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2.5 mb-4 text-sm">
                            <div className="flex justify-between text-gray-500"><span>Cəm</span><span className="font-mono">{fin.subtotal.toFixed(2)} ₼</span></div>
                            {fin.service_amount > 0 && (
                                <div className="flex justify-between text-gray-500"><span>Servis ({fin.service_percent}%)</span><span className="font-mono">{fin.service_amount.toFixed(2)} ₼</span></div>
                            )}
                            {fin.discount > 0 && (
                                <div className="flex justify-between text-rose-600"><span>Endirim</span><span className="font-mono">−{fin.discount.toFixed(2)} ₼</span></div>
                            )}
                            <div className="flex justify-between font-bold text-gray-800 border-t border-gray-200 mt-1.5 pt-1.5"><span>Ümumi</span><span className="font-mono">{fin.grand_total.toFixed(2)} ₼</span></div>
                        </div>

                        {/* Endirim — ONLINE ilə eyni: yalnız admin rolu (Bill.jsx canEditCharges, backend verifyAdmin).
                            Kassir / administrator / PIN ilə girən admin endirim verə bilmir. */}
                        {['admin', 'superadmin'].includes(user?.role) && (
                            <div className="mb-4">
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Endirim (₼)</label>
                                <input type="number" inputMode="decimal" step="0.01" min="0"
                                    value={offPayModal.discount ?? ''}
                                    onChange={(e) => setOffPayModal((p) => ({ ...p, discount: e.target.value }))}
                                    placeholder={!order?._offline && Number(order?.discount_amount || 0) > 0 ? Number(order.discount_amount).toFixed(2) : '0.00'}
                                    className="w-full rounded-xl border-2 border-gray-200 px-3 py-2 text-sm font-mono text-center focus:border-green-400 focus:outline-none" />
                            </div>
                        )}

                        <div className={`grid gap-2 mb-4 ${offDeliveryOnly ? 'grid-cols-1' : foodDelivery ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
                            {(offDeliveryOnly ? [['delivery', '🛵 Food delivery']] : [['nagd', '💵 Nağd'], ['kart', '💳 Kart'], ['hesab', '🔀 Bölünmüş'], ...(foodDelivery ? [['delivery', '🛵 Delivery']] : [])]).map(([m, label]) => (
                                <button key={m} type="button"
                                    onClick={() => setOffPayModal((p) => ({ ...p, method: m }))}
                                    className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${offPayModal.method === m ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        {offPayModal.method === 'hesab' && (
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <div>
                                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Nağd (₼)</label>
                                    <input type="number" inputMode="decimal" step="0.01" min="0" max={offPayTotal()} value={offPayModal.cash}
                                        onChange={(e) => {
                                            // Nağd cəmdən çox olmasın; kart = qalan (avtomatik)
                                            let v = e.target.value;
                                            const tot = offPayTotal();
                                            if (v !== '' && Number(String(v).replace(',', '.')) > tot) v = String(tot);
                                            setOffPayModal((p) => ({ ...p, cash: v }));
                                        }}
                                        placeholder="0.00"
                                        className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm font-mono text-center focus:border-green-400 focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Kart (₼)</label>
                                    <input type="text" readOnly tabIndex={-1}
                                        value={(() => {
                                            const c = Math.min(offPayTotal(), Math.max(0, Number(String(offPayModal.cash).replace(',', '.')) || 0));
                                            return (Math.round((offPayTotal() - c) * 100) / 100).toFixed(2);
                                        })()}
                                        className="w-full rounded-xl border-2 border-gray-100 bg-gray-50 px-3 py-2.5 text-sm font-mono text-center text-gray-700 cursor-not-allowed focus:outline-none" />
                                </div>
                            </div>
                        )}

                        {/* Offline-da bank terminalı da çox vaxt işləmir: sistem kartı ÇƏKMİR, yalnız qeyd edir */}
                        {cardPart && (
                            <label className="mb-4 flex cursor-pointer items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                                <input type="checkbox" checked={!!offPayModal.cardConfirmed}
                                    onChange={(e) => setOffPayModal((p) => ({ ...p, cardConfirmed: e.target.checked }))}
                                    className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600" />
                                <span><b>Kart məbləği bank terminalında çəkildi.</b> İnternet yoxdur: sistem kartı özü çəkmir, yalnız qeyd edir. Terminal işləmirsə nağd alın.</span>
                            </label>
                        )}

                        <div className="flex gap-3">
                            <button type="button" onClick={() => setOffPayModal({ ...offPayModal, open: false })}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                Ləğv et
                            </button>
                            <button type="button" onClick={confirmOfflineClose}
                                disabled={cardPart && !offPayModal.cardConfirmed}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                                style={{ background: 'linear-gradient(135deg, #1a7a40, #27AE60)' }}>
                                ✓ Bağla ({fin.grand_total.toFixed(2)} ₼)
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {noteModal && noteItem && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setNoteModal(false)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-base mb-1 text-gray-800">
                            📝 Qeyd — {noteItem.item_name}
                        </h3>
                        <p className="text-xs text-gray-400 mb-4">
                            Məs: soyuq olsun, şəkərsiz, əlavə sous...
                        </p>
                        <textarea
                            value={itemNote}
                            onChange={(e) => setItemNote(e.target.value)}
                            rows={3}
                            placeholder="Xüsusi istək yazın..."
                            autoFocus
                            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:border-primary-500 mb-4"
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={() => setNoteModal(false)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all"
                            >
                                Ləğv et
                            </button>
                            <button
                                onClick={saveItemNote}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                                style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                            >
                                ✓ Yadda saxla
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── ƏLAVƏ MENYU (manual item) MODAL ── */}
            {/* ── REPRINT TƏSDİQ MODAL ── */}
            {reprintConfirm.open && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
                     onClick={(e) => e.target === e.currentTarget && setReprintConfirm({ open: false, names: '' })}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="text-center mb-4">
                            <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center text-3xl mb-3">
                                ⚠️
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-1">Artıq mətbəxə göndərilib</h3>
                            <p className="text-xs text-gray-500">
                                Yenidən çap etmək təkrar yemək hazırlanmasına səbəb ola bilər
                            </p>
                        </div>

                        <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2.5 mb-4 max-h-32 overflow-y-auto">
                            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Göndərilmiş məhsullar</div>
                            <div className="text-sm font-medium text-gray-700">{reprintConfirm.names}</div>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => setReprintConfirm({ open: false, names: '' })}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                                Xeyr, ləğv et
                            </button>
                            <button
                                onClick={async () => {
                                    setReprintConfirm({ open: false, names: '' });
                                    await sendKitchenForce();
                                }}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                style={{ background: 'linear-gradient(135deg, #d35400, #e67e22)' }}>
                                🔄 Bəli, çap et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* POS ödəniş prosesi — bloklayan popup (yalnız imtina/xəta/uğurda bağlanır) */}
            {posModal.open && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
                     onClick={(e) => { if (posModal.closable && e.target === e.currentTarget) setPosModal({ open: false, message: '', closable: false, error: false, done: false }); }}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-center">
                        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4 ${
                            posModal.error ? 'bg-red-100' : posModal.done ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                            {posModal.error ? '❌' : posModal.done ? '✅' : (
                                <span className="inline-block h-8 w-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
                            )}
                        </div>
                        <h3 className="text-base font-bold text-gray-800 mb-1">💳 POS ödənişi</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{posModal.message}</p>
                        {posModal.closable ? (
                            <button
                                onClick={() => setPosModal({ open: false, message: '', closable: false, error: false, done: false })}
                                className={`mt-5 w-full py-2.5 rounded-xl text-sm font-bold text-white ${posModal.error ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-600 hover:bg-green-700'}`}>
                                Bağla
                            </button>
                        ) : (
                            <p className="mt-4 text-[11px] text-gray-400">Zəhmət olmasa gözləyin — prosesi dayandırmayın.</p>
                        )}
                    </div>
                </div>
            )}

            {/* Zala qayıdanda: çap olunmamış məhsullar var — göndərilsin? (superadmin tick) */}
            {exitConfirmModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
                     onClick={(e) => e.target === e.currentTarget && setExitConfirmModal(false)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="text-center mb-4">
                            <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-3xl mb-3">🖨️</div>
                            <h3 className="text-lg font-bold text-gray-800 mb-1">Mətbəxə göndərilməyən məhsullar var</h3>
                            <p className="text-xs text-gray-500">
                                Çap olunmamış məhsulları indi mətbəxə göndərək?
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setExitConfirmModal(false); doBackToTables(false); }}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                                Xeyr, göndərmə
                            </button>
                            <button
                                onClick={() => { setExitConfirmModal(false); doBackToTables(true); }}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-green-600 hover:bg-green-700">
                                ✓ Bəli, göndər
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {customItemModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
                     onClick={(e) => e.target === e.currentTarget && setCustomItemModal(false)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                        <h3 className="text-lg font-bold text-gray-800 mb-1">➕ Əlavə menyu</h3>
                        <p className="text-xs text-gray-500 mb-4">
                            Menyuya əlavə olunmadan birbaşa masaya yazılır
                        </p>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Məhsulun adı</label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={customItem.name}
                                    onChange={e => setCustomItem({ ...customItem, name: e.target.value })}
                                    placeholder="məs. Sirab (xüsusi), Pivə Tuborg"
                                    className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:outline-none"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Qiymət (₼)</label>
                                    <input
                                        type="number" step="0.01" min={0}
                                        value={customItem.price}
                                        onChange={e => setCustomItem({ ...customItem, price: e.target.value })}
                                        placeholder="0.00"
                                        className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Say (ədəd)</label>
                                    <input
                                        type="number" step="1" min={1}
                                        value={customItem.qty}
                                        onChange={e => setCustomItem({ ...customItem, qty: e.target.value })}
                                        className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:outline-none"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">Qeyd (ixtiyari)</label>
                                <input
                                    type="text"
                                    value={customItem.note}
                                    onChange={e => setCustomItem({ ...customItem, note: e.target.value })}
                                    className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:outline-none"
                                />
                            </div>

                            {/* Mətbəx printeri — tənzimləmələrdəki printerlər; 1 dənədirsə avtoseçili */}
                            {kitchenPrinters.length > 0 && (
                                <div>
                                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">🖨️ Mətbəx (printer)</label>
                                    <select
                                        value={customItem.printer}
                                        onChange={e => setCustomItem({ ...customItem, printer: e.target.value })}
                                        className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm bg-white focus:border-amber-400 focus:outline-none"
                                    >
                                        <option value="">— Mətbəxə göndərilməsin —</option>
                                        {kitchenPrinters.map(p => (
                                            <option key={p.name} value={p.name}>{p.label || p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {customItem.name && customItem.price && (
                                <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm">
                                    <span className="font-bold text-amber-800">{customItem.name}</span>
                                    <span className="text-amber-700"> × {customItem.qty}</span>
                                    <span className="float-right font-bold text-amber-800">
                                        {(parseFloat(customItem.price || 0) * parseFloat(customItem.qty || 1)).toFixed(2)} ₼
                                    </span>
                                </div>
                            )}

                            <div className="flex gap-2 mt-2">
                                <button onClick={() => setCustomItemModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">
                                    Ləğv et
                                </button>
                                <button onClick={submitCustomItem} disabled={customSaving}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                                    style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)' }}>
                                    {customSaving ? '...' : '➕ Əlavə et'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {qtyModal.open && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && closeQtyModal()}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">
                            {qtyModal.mode === 'add' ? 'Miqdar seçin' : 'Miqdarı dəyişin'}
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">
                            {(qtyModal.item?.name || qtyModal.orderItem?.item_name)} üçün miqdar daxil edin.
                        </p>

                        {/* Göstərici (OS klaviaturası açılmır — app keypadı ilə yazılır) */}
                        <input
                            type="text"
                            inputMode="none"
                            readOnly
                            value={qtyModal.value}
                            onKeyDown={(e) => { if (e.key === 'Enter') confirmQtyModal(); }}
                            placeholder="0"
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-2xl font-bold font-mono text-center focus:outline-none focus:border-primary-500 mb-4 cursor-default select-none"
                        />

                        {/* Sürətli dəyərlər */}
                        <div className="grid grid-cols-5 gap-2 mb-3">
                            {(qtyModal.quickValues || getQuickValues(qtyModal.saleType)).map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setQtyModal((prev) => ({ ...prev, value: String(value), fresh: false }))}
                                    className="py-2 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
                                >
                                    {value}
                                </button>
                            ))}
                        </div>

                        {/* App rəqəm keypadı */}
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {['1','2','3','4','5','6','7','8','9'].map((d) => (
                                <button key={d} type="button" onClick={() => qtyKeypadPress(d)}
                                    className="py-3 rounded-xl border border-gray-200 text-xl font-bold text-gray-800 hover:border-primary-400 hover:bg-primary-50 active:scale-95 transition-all">
                                    {d}
                                </button>
                            ))}
                            <button type="button" onClick={() => qtyKeypadPress('.')}
                                disabled={qtyModal.saleType !== 'weight' && qtyModal.saleType !== 'liter'}
                                className="py-3 rounded-xl border border-gray-200 text-xl font-bold text-gray-800 hover:border-primary-400 hover:bg-primary-50 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-transparent">
                                .
                            </button>
                            <button type="button" onClick={() => qtyKeypadPress('0')}
                                className="py-3 rounded-xl border border-gray-200 text-xl font-bold text-gray-800 hover:border-primary-400 hover:bg-primary-50 active:scale-95 transition-all">
                                0
                            </button>
                            <button type="button" onClick={() => qtyKeypadPress('back')}
                                className="py-3 rounded-xl border border-gray-200 text-xl font-bold text-red-500 hover:border-red-300 hover:bg-red-50 active:scale-95 transition-all">
                                ⌫
                            </button>
                        </div>

                        <div className="flex items-center justify-between mb-4">
                            <span className="text-xs text-gray-400">
                                Vahid: {qtyModal.saleType === 'weight' ? 'kq' : qtyModal.saleType === 'liter' ? 'lt' : 'əd'}
                            </span>
                            <button type="button" onClick={() => qtyKeypadPress('C')}
                                className="text-xs font-bold text-gray-400 hover:text-red-500">
                                Təmizlə
                            </button>
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={closeQtyModal}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                            >
                                Ləğv et
                            </button>
                            <button
                                type="button"
                                onClick={confirmQtyModal}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                            >
                                {qtyModal.mode === 'add' ? 'Əlavə et' : 'Yadda saxla'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {unlockModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setUnlockModal(false)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">Çap kilidini aç</h3>
                        <p className="text-sm text-gray-400 mb-4">
                            Yenidən çapa icazə vermək istədiyiniz mətbəxi seçin.
                        </p>
                        <div className="flex flex-col gap-2">
                            {[
                                { key: 'xp-metbex', label: '🔥 İsti mətbəx' },
                                { key: 'xp-soyuq', label: '❄️ Soyuq mətbəx' },
                                { key: 'xp-barmen', label: '🍹 Barmen' }
                            ].map((printer) => (
                                <button
                                    key={printer.key}
                                    onClick={() => unlockKitchenPrinter(printer.key)}
                                    className="w-full py-3 rounded-xl border border-amber-200 text-amber-700 font-semibold hover:bg-amber-50 transition-all"
                                >
                                    {printer.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {moveModal && (() => {
                const isMerge = moveMode === 'merge';
                const list = isMerge ? mergeTargets : moveTargets;
                const selectedId = isMerge ? selectedMergeOrderId : selectedMoveTableId;
                const q = moveSearch.toLowerCase().trim();
                const shown = !q ? list : list.filter((table) =>
                    String(table.name || '').toLowerCase().includes(q)
                    || String(table.number || '').includes(q)
                    || String(table.room_name || '').toLowerCase().includes(q)
                    || String(table.waiter_name || '').toLowerCase().includes(q));
                const selectedMerge = isMerge ? mergeTargets.find((t) => String(t.order_id) === String(selectedMergeOrderId)) : null;
                const busy = movingOrder || mergeLoading;
                return (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setMoveModal(false)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
                        <h3 className="font-bold text-lg mb-3 text-gray-800">{isMerge ? 'Dolu masa ilə birləşdir' : 'Boş masaya köçür'}</h3>

                        {/* Rejim: boş masaya köçür / dolu masa ilə birləşdir */}
                        <div className="mb-3 grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1">
                            {[['move', 'Boş masaya köçür'], ['merge', 'Dolu masa ilə birləşdir']].map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => switchMoveMode(key)}
                                    className={`rounded-lg px-2 py-2 text-xs font-semibold transition-all ${
                                        moveMode === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                            {isMerge
                                ? 'Bu masanın məhsulları seçdiyiniz masaya əlavə olunacaq, bu masa boşalacaq. Tam eyni məhsullar (qeyd, qiymət, variant eyni) bir sətirdə toplanır.'
                                : 'Yalnız boş və uyğun masalar göstərilir.'}
                        </p>

                        {list.length > 5 && (
                            <div className="relative mb-3">
                                <input
                                    type="text"
                                    value={moveSearch}
                                    onChange={(e) => setMoveSearch(e.target.value)}
                                    placeholder="🔍 Masa axtar..."
                                    autoFocus
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2 pr-9 text-sm focus:outline-none focus:border-primary-400"
                                />
                                {moveSearch && (
                                    <button type="button" onClick={() => setMoveSearch('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs">
                                        ✕
                                    </button>
                                )}
                            </div>
                        )}

                        {mergeLoading && isMerge ? (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-5 text-sm text-center text-gray-500">
                                Yüklənir...
                            </div>
                        ) : list.length === 0 ? (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-5 text-sm text-center text-gray-500">
                                {isMerge ? 'Birləşdirmək üçün dolu masa tapılmadı.' : 'Uyğun boş masa tapılmadı.'}
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-72 overflow-y-auto">
                                {shown.map((table) => {
                                    const key = isMerge ? table.order_id : table.id;
                                    const active = String(key) === String(selectedId);
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => (isMerge ? setSelectedMergeOrderId(String(key)) : setSelectedMoveTableId(String(key)))}
                                            className={`w-full rounded-xl border px-4 py-3 text-left transition-all ${
                                                active ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-primary-300'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="font-semibold text-gray-800">{table.name}</div>
                                                {isMerge && (
                                                    <div className="flex-shrink-0 text-sm font-bold text-gray-800">{Number(table.total || 0).toFixed(2)} ₼</div>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                № {table.number}{table.room_name ? ` · ${table.room_name}` : ''}
                                                {isMerge && ` · ${table.item_count} məhsul${table.waiter_name ? ` · ${table.waiter_name}` : ''}`}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {isMerge && selectedMerge && (
                            <div className="mt-3 rounded-xl bg-amber-50 px-3 py-2.5 text-xs font-medium text-amber-800 ring-1 ring-amber-200">
                                {(order?.table_name || 'Bu masa')} → <b>{selectedMerge.name}</b>. Bu masa boşalacaq, hesab {selectedMerge.name} masasında davam edəcək.
                            </div>
                        )}

                        <div className="mt-5 flex gap-3">
                            <button
                                onClick={() => setMoveModal(false)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                            >
                                Geri
                            </button>
                            <button
                                onClick={isMerge ? mergeOrderIntoTable : moveOrderToTable}
                                disabled={!selectedId || busy || list.length === 0}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ background: isMerge ? 'linear-gradient(135deg, #b45309, #f59e0b)' : 'linear-gradient(135deg, #006f91, #0090bb)' }}
                            >
                                {isMerge ? (movingOrder ? 'Birləşdirilir...' : 'Birləşdir') : (movingOrder ? 'Köçürülür...' : 'Köçür')}
                            </button>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* ── Ofisianti dəyiş modalı ──────────────────────────────── */}
            {waiterChangeModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setWaiterChangeModal(false)}
                >
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
                        <div className="border-b border-gray-100 px-5 py-4">
                            <h3 className="font-bold text-base text-gray-800">👤 Ofisianti dəyiş</h3>
                            <p className="mt-0.5 text-xs text-gray-400">Bu sifariş üçün yeni ofisiant seçin</p>
                        </div>

                        {waiterChangeList.length > 5 && (
                            <div className="px-4 pt-3 relative">
                                <input
                                    type="text"
                                    value={waiterChangeSearch}
                                    onChange={(e) => setWaiterChangeSearch(e.target.value)}
                                    placeholder="🔍 Ofisiant axtar..."
                                    autoFocus
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2 pr-9 text-sm focus:outline-none focus:border-violet-400"
                                />
                                {waiterChangeSearch && (
                                    <button
                                        type="button"
                                        onClick={() => setWaiterChangeSearch('')}
                                        className="absolute right-6 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                            {waiterChangeLoading ? (
                                <p className="text-center text-sm text-gray-400 py-6">Yüklənir...</p>
                            ) : waiterChangeList.length === 0 ? (
                                <p className="text-center text-sm text-gray-400 py-6">Aktiv ofisiant tapılmadı.</p>
                            ) : (
                                waiterChangeList
                                    .filter(w => !waiterChangeSearch.trim() || String(w.name || '').toLowerCase().includes(waiterChangeSearch.toLowerCase().trim()))
                                    .map((w) => {
                                        const isCurrent = Number(order?.user_id) === Number(w.id);
                                        return (
                                            <button
                                                key={w.id}
                                                disabled={waiterChangeSaving || isCurrent}
                                                onClick={() => changeWaiter(w.id)}
                                                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all disabled:opacity-50 ${
                                                    isCurrent
                                                        ? 'border-violet-300 bg-violet-50'
                                                        : 'border-slate-200 hover:bg-violet-50 hover:border-violet-300'
                                                }`}
                                            >
                                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 font-bold text-sm">
                                                    {w.name?.[0] || '?'}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-semibold text-sm text-slate-800 truncate">{w.name}</p>
                                                    <p className="text-xs text-slate-400">{isCurrent ? 'Cari ofisiant' : 'Ofisiant'}</p>
                                                </div>
                                                {!isCurrent && <span className="text-violet-400 text-lg">›</span>}
                                            </button>
                                        );
                                    })
                            )}
                        </div>

                        <div className="border-t border-gray-100 px-5 py-3">
                            <button
                                onClick={() => setWaiterChangeModal(false)}
                                className="w-full rounded-xl border border-slate-200 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50"
                            >
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {printLogModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setPrintLogModal(false)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-base text-gray-800 mb-1">🧾 Çek çap tarixçəsi</h3>
                        <p className="text-xs text-gray-400 mb-4">Masa: {order?.table_name || '-'} · #{currentOrderId}</p>
                        {printLogLoading ? (
                            <div className="py-6 text-center text-gray-400 text-sm">Yüklənir...</div>
                        ) : printLog.length === 0 ? (
                            <div className="py-6 text-center text-gray-400 text-sm">Hələ çek çap olunmayıb.</div>
                        ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                                {printLog.map((r, i) => (
                                    <div key={r.id} className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2">
                                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-600">{i + 1}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-gray-800">
                                                {r.type === 'gift' ? '🎟️ Yarışma çeki' : '🖨️ Müştəri çeki'}
                                            </div>
                                            <div className="text-[11px] text-gray-400 truncate">
                                                {fmtLogTime(r.printed_at)}{r.printed_by_name ? ` · ${r.printed_by_name}` : ''}
                                            </div>
                                        </div>
                                        {r.type === 'gift' && (
                                            <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold text-green-600">uğurlu</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <button onClick={() => setPrintLogModal(false)}
                            className="mt-4 w-full py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                            Bağla
                        </button>
                    </div>
                </div>
            )}

            {cancelModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setCancelModal(false)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">Sifarişi ləğv et</h3>
                        <p className="text-sm text-gray-500 mb-5">
                            Bu sifariş tam silinəcək. Davam etmək istəyirsiniz?
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setCancelModal(false)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                            >
                                Geri
                            </button>
                            <button
                                onClick={cancelOrder}
                                className="flex-1 py-2.5 rounded-xl bg-red-500 text-sm font-bold text-white hover:bg-red-600"
                            >
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* VARIANT PICKER MODAL */}
            {variantModal.open && variantModal.item && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setVariantModal({ open: false, item: null })}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-base mb-1 text-gray-800">
                            {variantModal.item.name}
                        </h3>
                        <p className="text-xs text-gray-400 mb-4">Variant seçin</p>

                        <div className="flex flex-col gap-2">
                            {variantModal.item.variants.map((v) => (
                                <button
                                    key={v.id}
                                    onClick={() => {
                                        const item = variantModal.item;
                                        const variant = v;
                                        setVariantModal({ open: false, item: null });
                                        addItem(item, variant);
                                    }}
                                    className="flex items-center justify-between px-4 py-3 rounded-xl border-2 border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition-all text-left"
                                >
                                    <span className="font-semibold text-gray-700">{v.name}</span>
                                    <span className="font-bold font-mono text-primary-600">{parseFloat(v.price).toFixed(2)} ₼</span>
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => setVariantModal({ open: false, item: null })}
                            className="mt-4 w-full py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all"
                        >
                            Ləğv et
                        </button>
                    </div>
                </div>
            )}

            {/* SADƏ SATIŞ — qiymət modal */}
            {openPriceModal.open && openPriceModal.item && (
                <div
                    className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setOpenPriceModal({ open: false, item: null, price: '' })}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-base mb-1 text-gray-800">{openPriceModal.item.name}</h3>
                        <p className="text-xs text-gray-400 mb-4">Qiymət daxil edin (₼)</p>
                        <input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            autoFocus
                            value={openPriceModal.price}
                            onChange={(e) => setOpenPriceModal({ ...openPriceModal, price: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const p = parseFloat(openPriceModal.price);
                                    if (!p || p <= 0) { toast.error('Düzgün qiymət daxil edin.'); return; }
                                    submitItemToOrder(openPriceModal.item, 1, p);
                                    setOpenPriceModal({ open: false, item: null, price: '' });
                                }
                            }}
                            placeholder="0.00"
                            className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-lg font-bold font-mono text-center focus:border-primary-400 focus:outline-none"
                        />
                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={() => setOpenPriceModal({ open: false, item: null, price: '' })}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                            >
                                Ləğv et
                            </button>
                            <button
                                onClick={() => {
                                    const p = parseFloat(openPriceModal.price);
                                    if (!p || p <= 0) { toast.error('Düzgün qiymət daxil edin.'); return; }
                                    submitItemToOrder(openPriceModal.item, 1, p);
                                    setOpenPriceModal({ open: false, item: null, price: '' });
                                }}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-primary-600 hover:bg-primary-700"
                            >
                                Əlavə et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* WhatsApp — sifarişi göndər */}
            {waModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
                     onClick={(e) => e.target === e.currentTarget && setWaModal(false)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
                        <div className="border-b border-gray-100 px-5 py-4">
                            <h3 className="font-bold text-gray-800">📱 WhatsApp-a göndər</h3>
                            <p className="mt-0.5 text-xs text-gray-400">{order?.table_name || 'Masa'} sifarişi</p>
                        </div>
                        <div className="space-y-3 p-5">
                            <div>
                                <label className="text-[11px] font-semibold text-gray-500">Nömrə</label>
                                <input
                                    type="tel"
                                    value={waNum}
                                    onChange={(e) => setWaNum(e.target.value)}
                                    placeholder="Nömrə daxil edin"
                                    autoFocus
                                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-mono focus:border-green-400 focus:outline-none"
                                />
                            </div>
                            {waHistory.length > 0 && (
                                <div>
                                    <p className="mb-1.5 text-[11px] font-semibold text-gray-500">Son göndərilənlər</p>
                                    <div className="flex flex-wrap gap-2">
                                        {waHistory.map((n) => (
                                            <button key={n} type="button" onClick={() => setWaNum(n)}
                                                className={`rounded-full border px-3 py-1.5 text-xs font-mono font-semibold transition-all ${
                                                    waNum === n
                                                        ? 'border-green-600 bg-green-600 text-white'
                                                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-green-300 hover:bg-green-50'
                                                }`}>
                                                📲 {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-100 bg-gray-50 p-2.5 font-mono text-[11px] text-gray-500">
                                {buildOrderWhatsappMessage()}
                            </div>
                        </div>
                        <div className="flex gap-2 border-t border-gray-100 px-5 py-4">
                            <button onClick={() => setWaModal(false)}
                                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-bold text-gray-500 hover:bg-gray-50">
                                Bağla
                            </button>
                            <button onClick={sendOrderWhatsapp} disabled={waSending}
                                className="flex-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50">
                                {waSending ? 'Göndərilir...' : '📤 Göndər'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Banket əlavə et */}
            {banquetModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
                     onClick={(e) => e.target === e.currentTarget && setBanquetModal(false)}>
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="border-b border-gray-100 px-5 py-4">
                            <h3 className="text-lg font-bold text-gray-800">🍽️ Banket əlavə et</h3>
                            <p className="text-xs text-gray-400">Banket seç, qonaq sayını yaz</p>
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto p-5">
                            {banquets.length === 0 ? (
                                <p className="py-6 text-center text-sm text-gray-400">Aktiv banket yoxdur. Admin → Banket menyu.</p>
                            ) : (
                                <div className="space-y-2">
                                    {banquets.map((b) => (
                                        <button key={b.id} type="button" onClick={() => selectBanquet(b)}
                                            className={`flex w-full items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-all ${
                                                bqSel?.id === b.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                            <div>
                                                <div className="text-sm font-bold text-gray-800">{b.name}</div>
                                                <div className="text-[11px] text-gray-400">
                                                    {b.items?.length || 0} məhsul
                                                    {Number(b.min_guests || 0) > 0 && <span className="ml-1.5 font-bold text-amber-600">· min {Number(b.min_guests)} nəfər</span>}
                                                </div>
                                            </div>
                                            <span className="text-sm font-black text-primary-700">{Number(b.price_per_person).toFixed(0)} ₼/nəfər</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {bqSel && (
                                <div className="mt-4 rounded-xl border border-gray-200 p-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-bold text-gray-800">Qonaq sayı</span>
                                        <div className="flex items-center gap-3">
                                            <button type="button" onClick={() => setBqGuests((g) => Math.max(bqFloor, Number(g) - 1))}
                                                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-primary-300 text-lg font-black text-primary-600">−</button>
                                            <input type="number" min={bqFloor} value={bqGuests}
                                                onChange={(e) => setBqGuests(e.target.value)}
                                                className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-center text-lg font-black" />
                                            <button type="button" onClick={() => setBqGuests((g) => Math.max(1, Number(g) + 1))}
                                                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-primary-300 text-lg font-black text-primary-600">+</button>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex justify-between border-t border-gray-100 pt-2 text-sm font-black text-primary-700">
                                        <span>Cəmi (servissiz)</span>
                                        <span>{(Number(bqSel.price_per_person) * Math.max(1, parseInt(bqGuests, 10) || 0)).toFixed(2)} ₼</span>
                                    </div>
                                    {bqBelowMin && (
                                        <div className={`mt-2 rounded-lg px-3 py-2 text-xs font-semibold ${bqCanBelowMin ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>
                                            ⚠️ Minimum qonaq sayının ({bqMin} nəfər) altına düşdünüz.
                                            {bqCanBelowMin ? ' Admin kimi yenə də əlavə edə bilərsiniz.' : ' Daha az sayı yalnız admin yaza bilər.'}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 border-t border-gray-100 px-5 py-4">
                            <button onClick={() => setBanquetModal(false)}
                                className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-bold text-gray-500 hover:bg-gray-50">Bağla</button>
                            <button onClick={submitBanquet} disabled={!bqSel || bqBusy || (bqBelowMin && !bqCanBelowMin)}
                                className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition-all disabled:opacity-50"
                                style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                {bqBusy ? 'Əlavə olunur...' : '✓ Sifarişə əlavə et'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="Bu funksiya Pulsuz paketdə bağlıdır"
                message="Mətbəxə göndərmə və müştəri çeki çapı üçün Sadə və ya Pro plana keçin."
                plans={['basic', 'pro']}
                currentPlan={user?.tenant_plan}
            />
        </div>
    );
};

const CategoryCard = ({ category, accent, onOpen }) => (
    <button
        onClick={onOpen}
        className={`text-left rounded-3xl border bg-gradient-to-br ${accent.shell} p-2.5 min-h-[85px] 2xl:min-h-[108px] hover:-translate-y-0.5 hover:shadow-md transition-all`}
    >
        <div className="flex items-start justify-between mb-3">
            {category.image_url ? (
                <img
                    src={toAssetUrl(category.image_url)}
                    alt={category.name}
                    className="w-10 h-10 rounded-2xl object-cover border border-white/70 shadow-sm"
                />
            ) : (
                <div className="w-10 h-10 rounded-2xl bg-white/80 border border-white/70 flex items-center justify-center text-lg shadow-sm">
                    {getCategoryIcon(category.name)}
                </div>
            )}
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${accent.badge}`}>
                {category.items.length}
            </span>
        </div>

        <div className="font-bold text-sm text-gray-800 leading-snug mb-1">
            {category.name}
        </div>
        <div className="text-[11px] text-gray-500 leading-snug line-clamp-2">
            Məhsullara keçin və sürətli seçim edin.
        </div>
    </button>
);

const MenuItemCard = ({ item, onAdd }) => (
    <div
        onClick={() => onAdd(item)}
        className="bg-white border border-gray-200 rounded-2xl p-2 2xl:p-3 cursor-pointer transition-all hover:border-primary-300 hover:-translate-y-0.5 hover:shadow-md active:scale-95"
    >
        <div
            className="aspect-[4/3] max-h-[280px] rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-100 flex items-center justify-center mb-2 relative overflow-hidden"
            style={{ aspectRatio: '4 / 3' }}
        >
            {item.image_url ? (
                <img
                    src={toAssetUrl(item.image_url)}
                    alt={item.name}
                    className="absolute inset-0 w-full h-full object-cover"
                />
            ) : (
                <>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.8),_transparent_65%)]"></div>
                    <div className="relative text-4xl">
                        {item.is_set ? '🎁' : item.sale_type === 'weight' ? '⚖️' : item.sale_type === 'liter' ? '🥤' : '🍽️'}
                    </div>
                </>
            )}
            <div className="absolute top-2 right-2 w-8 h-8 rounded-xl bg-gray-900 text-white flex items-center justify-center text-base font-bold shadow-sm">
                +
            </div>
        </div>

        <div className="font-semibold text-sm text-gray-800 leading-tight min-h-[40px]">
            {item.name}
        </div>

        {item.description && (
            <div className="text-xs text-gray-400 mt-1 line-clamp-2 min-h-[32px]">
                {item.description}
            </div>
        )}

        {/* Variantlı məhsul: tək kiçik sətir (qiymətsiz). Adi məhsul: qiymət + satış növü */}
        {item.variants?.length > 0 ? (
            <div className="mt-3 text-xs font-semibold text-purple-600">
                Variant seç · {item.variants.length} variant →
            </div>
        ) : (
            <div className="flex items-center justify-between gap-2 mt-3">
                <div className="font-bold font-mono text-primary-600 text-base">
                    {parseFloat(item.price).toFixed(2)} ₼
                </div>
                {!item.is_set && (
                    <span className="px-2 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-gray-100 text-gray-500">
                        {getSaleTypeBadge(item.sale_type)}
                    </span>
                )}
            </div>
        )}
    </div>
);

export default WaiterOrder;
